import React, { useMemo, useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Palette, Ruler, Sun, Moon, Move, Maximize2, Upload, X, Shapes, Circle, ArrowUpRight, RotateCw, RotateCcw, Plus, Trash2, Copy, Box, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useComposition } from "../contexts/CompositionContext";
import CustomizableSvg from "./CustomizableSvg";
import CompositionOverlay from "./CompositionOverlay";
import SceneCategoryPicker from "./SceneCategoryPicker";
import MobileDesktopHint from "./MobileDesktopHint";
import { useIsMobile } from "../hooks/useIsMobile";
import ProjectsToolbar from "./ProjectsToolbar";
import GlobalPricePanel from "./GlobalPricePanel";
import CROSSES_SVG_MANIFEST from "../data/crosses-svg-manifest.json";
import RONDES_SVG_MANIFEST from "../data/rondes-svg-manifest.json";
import RONDES_V2_MANIFEST from "../data/rondes-svg-v2-manifest.json";
import CROIX_V3_MANIFEST from "../data/croix-v3-manifest.json";
import CROIX_SIMPLE_MANIFEST from "../data/croix-simple-manifest.json";
import CROIX_NEON_MANIFEST from "../data/croix-neon-manifest.json";
import RONDE_NEON_MANIFEST from "../data/ronde-neon-manifest.json";

// Helper: expand the user's slot-based customColors override into a per-class
// override map that CustomizableSvg understands. Each color slot may map to
// multiple original CSS classes that share the same default color.
function expandCustomColors(design, customColors) {
  if (!customColors || !design?.colorLayers) return {};
  const out = {};
  for (const layer of design.colorLayers) {
    const userHex = customColors[layer.id];
    if (!userHex) continue;
    for (const cls of layer.classes || []) {
      out[cls] = userHex;
    }
  }
  return out;
}

// Helper: expand customColors into a per-element ID override map (used by the
// new ronde-v2 designs where each slot targets specific SVG element IDs).
function expandIdOverrides(design, customColors) {
  if (!customColors || !design?.colorLayers) return {};
  const out = {};
  for (const layer of design.colorLayers) {
    // Skip synthetic layers — they are handled via <use> overlays (not CSS)
    if (layer.synthetic) continue;
    const userHex = customColors[layer.id];
    if (!userHex) continue;
    for (const id of layer.targetIds || []) {
      out[id] = userHex;
    }
  }
  return out;
}

// Helper: build the list of <use> overlay descriptors for synthetic layers
// (typically the "Couleur intérieur" slot on solid crosses without a built-in
// interior path). Each overlay references the source path and applies a
// scale-down transform around its center, with the user-selected color.
function expandSyntheticOverlays(design, customColors) {
  if (!design?.colorLayers) return [];
  const out = [];
  for (const layer of design.colorLayers) {
    if (!layer.synthetic) continue;
    const userHex = customColors?.[layer.id] || layer.default;
    if (!userHex || userHex === "transparent" || userHex === "__transparent__") continue;
    for (const id of layer.targetIds || []) {
      out.push({
        id,
        sourceId: layer.synthetic.sourceId,
        scale: layer.synthetic.scale,
        color: userHex,
      });
    }
  }
  return out;
}

// All designs in a single sprite SVG share the same color slots (classes).
function buildCustomizableDesign({ manifest, idx, family, labelPrefix, spritePath }) {
  const design = manifest.designs[idx - 1];
  if (!design) return null;
  return {
    id: `${labelPrefix.toLowerCase()}-${idx}`,
    label: { fr: `${labelPrefix} ${idx}` },
    family,
    customizableSvg: true,
    svgPath: spritePath,
    viewBox: design.viewBox,
    colorLayers: manifest.layers.map((L) => ({ id: L.id, label: L.label, default: L.default, classes: L.classes })),
  };
}

// Palette de nuances pour la personnalisation des designs SVG multi-couches
// Organisée par famille de couleur pour faciliter la sélection
// Palette néon réduite (8 couleurs vives) — utilisée uniquement pour les
// designs "néon LED" (Croix néon + Ronde médaillon néon, identifiés par leur
// id `croix-neon-N` / `ronde-neon-N`). Les autres designs (Croix Flèche, V3,
// Simple, Multi, etc.) continuent d'utiliser la palette complète CUSTOM_PALETTE.
export const NEON_PALETTE = [
  { id: "vert-neon",  hex: "#39FF14", label: "Vert néon",  family: "vert" },
  { id: "bleu-fonce", hex: "#1e5fff", label: "Bleu foncé", family: "bleu" },
  { id: "bleu-clair", hex: "#00e7ff", label: "Bleu clair", family: "bleu" },
  { id: "rouge",      hex: "#ff1a1a", label: "Rouge",      family: "rouge" },
  { id: "orange",     hex: "#ff7a18", label: "Orange",     family: "orange" },
  { id: "jaune",      hex: "#fff200", label: "Jaune",      family: "jaune" },
  { id: "blanc",      hex: "#ffffff", label: "Blanc",      family: "neutre" },
  { id: "rose-neon",  hex: "#ff00c8", label: "Rose néon",  family: "rose" },
];

export const CUSTOM_PALETTE = [
  // Verts
  { id: "vert-neon", hex: "#39FF14", label: "Vert néon", family: "vert" },  { id: "vert-vif", hex: "#64ff00", label: "Vert vif", family: "vert" },
  { id: "vert-classique", hex: "#1aaa2a", label: "Vert classique", family: "vert" },
  { id: "vert-fonce", hex: "#169300", label: "Vert foncé", family: "vert" },
  { id: "vert-foret", hex: "#0c4a1a", label: "Vert forêt", family: "vert" },
  { id: "vert-sapin", hex: "#00700b", label: "Vert sapin", family: "vert" },
  // Bleus
  { id: "bleu-cyan", hex: "#00bfff", label: "Cyan", family: "bleu" },
  { id: "bleu-ciel", hex: "#5ec8ff", label: "Bleu ciel", family: "bleu" },
  { id: "bleu-royal", hex: "#1e5fff", label: "Bleu royal", family: "bleu" },
  { id: "bleu-marine", hex: "#0a2a6e", label: "Bleu marine", family: "bleu" },
  { id: "bleu-petrole", hex: "#003a5c", label: "Bleu pétrole", family: "bleu" },
  // Rouges
  { id: "rouge-vif", hex: "#ff2a2a", label: "Rouge vif", family: "rouge" },
  { id: "rouge-pharmacie", hex: "#c32424", label: "Rouge pharmacie", family: "rouge" },
  { id: "rouge-bordeaux", hex: "#7a0f0f", label: "Bordeaux", family: "rouge" },
  // Oranges
  { id: "orange-vif", hex: "#ff7a18", label: "Orange vif", family: "orange" },
  { id: "orange-abricot", hex: "#ff9a3c", label: "Abricot", family: "orange" },
  // Jaunes
  { id: "jaune-vif", hex: "#fff200", label: "Jaune vif", family: "jaune" },
  { id: "jaune-or", hex: "#ffb800", label: "Or", family: "jaune" },
  // Neutres
  { id: "blanc", hex: "#ffffff", label: "Blanc", family: "neutre" },
  { id: "gris-perle", hex: "#dcdcdc", label: "Gris perle", family: "neutre" },
  { id: "noir", hex: "#0a0a0a", label: "Noir", family: "neutre" },
];

// Scènes intérieures (placeholders — l'utilisateur fournira ses propres photos)
// Pour l'instant on utilise les façades existantes recyclées comme arrière-plan neutre
export const CROSS_SCENES = [
  // Façades d'angle — pharmacies en coin de rue (pour les enseignes d'angle)
  { id: "corner-artdeco", label: { fr: "Pharmacie d'angle Art Déco" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/gjjfo9f4_3a625685-82df-4367-9aae-3701bc145f83.png" },
  { id: "corner-dark-metal", label: { fr: "Pharmacie d'angle bardage anthracite" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/70uzpnvr_2922c193-f80f-4c54-bb14-e10941816e5d.png" },
  { id: "corner-white-metal", label: { fr: "Pharmacie d'angle bardage blanc" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/j6dxj9ny_aea2b1c7-bb18-4ede-9341-e4b76d496474.png" },
  { id: "corner-haussmann", label: { fr: "Pharmacie d'angle haussmannienne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/ogau3h2u_d9737a23-b900-4a73-b717-c144090e8eca.png" },
  { id: "corner-modern-cube", label: { fr: "Pharmacie d'angle cube moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/v4rmuot4_fdc965ca-7ab5-48a1-b74e-8ecd4fb0fa46.png" },
  // Façades classiques — 3 vignettes d'aperçu en premier (immeuble / maison / bâtiment)
  { id: "facade-modern-white", label: { fr: "Immeuble blanc moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/yf56ryw8_a.png" },
  { id: "stone-chalet", label: { fr: "Maison chalet pierre" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/s9deqq2q_0f8b84a2-7f8d-41a2-8821-6bea645ff457.png" },
  { id: "black-cladding", label: { fr: "Bâtiment bardage noir (tôlé)" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/x6apqhu6_84390b14-6569-4b52-abf9-8d12ed5aa6fa.png" },
  // Suite des façades (déroulant)
  { id: "facade-haussmann", label: { fr: "Façade haussmannienne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/6mhdwjth_b.png" },
  { id: "facade-classique", label: { fr: "Façade classique parisienne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/7v2fytnw_c.png" },
  { id: "beige-modern", label: { fr: "Façade beige" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/mi297px7_99ea53b8-9a9d-4271-9c68-d1f363fa4106.png" },
  { id: "haussmann", label: { fr: "Haussmann" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/js4kaf7b_338b5889-2bea-48ab-98e1-809dfe58103b.png" },
  { id: "white-bardage", label: { fr: "Bardage blanc" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/qklpcsiy_353fdca0-8632-4559-bd4f-737a5c3f9182.png" },
  { id: "wood-chalet", label: { fr: "Chalet bois" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/5kogabje_1658e095-2d56-421a-b178-58cab18d78bf.png" },
  { id: "alpine-pharmacy", label: { fr: "Chalet alpin" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/0k38j41v_36478937-d113-4ddd-824c-5442cb0ac647.png" },
  { id: "stone-classic", label: { fr: "Pierre classique" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/x2amcont_bd107970-d443-417b-b6e6-0519d1429f35.png" },
  { id: "cream-haussmann", label: { fr: "Façade crème" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/u7h5rrku_c9dd0a4d-b661-4c3e-844b-c6d7414648fb.png" },
  { id: "white-modern", label: { fr: "Immeuble blanc" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/bs7nark0_ea3a81f0-d664-43a7-ab34-5061e68a8854.png" },
  { id: "stone-mountain", label: { fr: "Pierre montagne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/whi8hoxp_f809e244-9835-40d5-9d2e-81ef50d5d803.png" },
  { id: "white-pharma", label: { fr: "Pharmacie moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/5yqyhz5i_3134e1cd-9628-4a46-a466-f7b7829e6e70.png" },
  { id: "wood-shopfront", label: { fr: "Devanture bois" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/oipht28q_5831c5f3-234e-425a-b60c-bab4d1648c59.png" },
  { id: "white-shopfront", label: { fr: "Devanture blanche" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/9g4lmldi_72511b8f-c69c-4389-98e8-657891dac452.png" },
  // Intérieurs de pharmacie
  { id: "interior-counter-twin", label: { fr: "Intérieur — comptoirs jumeaux" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/f7fa791l_4ac36972-b2fb-4edf-8554-1e28ec6d337d.png" },
  { id: "interior-counter-boxes", label: { fr: "Intérieur — comptoirs centraux" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/3sblft3b_4c81caa0-8255-48f6-a357-df689f3f69da.png" },
  { id: "interior-zen-beige", label: { fr: "Intérieur zen beige" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/2acemw6p_7b867c05-e007-4772-bc72-7f8e6e9e1348.png" },
  { id: "interior-modern-white", label: { fr: "Intérieur blanc moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/tvb6cqqu_8ae97268-566c-4e54-bfd7-2123bc48ea4e.png" },
  { id: "interior-warm-wood", label: { fr: "Intérieur bois chaleureux" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/23s60spf_69cfc849-6ebe-4424-9a5f-4697212cba4b.png" },
  { id: "interior-zen-counter-low", label: { fr: "Intérieur zen — comptoir bas" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/xnkzak0u_c5d1ebb2-4b46-4bab-906d-c217253fdbef.png" },
  { id: "interior-classic-wood", label: { fr: "Intérieur classique bois foncé" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/x53ofjdi_d95e1c7e-03d4-4073-8d8a-36af98029575.png" },
  { id: "interior-high-ceiling", label: { fr: "Intérieur haut plafond vert" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/0wcgxa83_ddde0c3b-868f-4e87-a675-a24e7d493e37.png" },
  { id: "interior-luxe-modern", label: { fr: "Intérieur luxe moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/12ttou6z_f1282b4f-35b0-4914-81c9-304fd55906cd.png" },
  { id: "interior-mountain-wood", label: { fr: "Intérieur chalet montagne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/zpncjwnd_fc5c325a-9c2a-4528-91d9-e6ea43a0f425.png" },
];

// Couleurs néon LED haute luminosité (8 couleurs sélectionnées Signapharma)
export const CROSS_COLORS = [
  { id: "vert",       label: { fr: "Vert" },        hex: "#22FF00" },
  { id: "bleu-fonce", label: { fr: "Bleu foncé" },  hex: "#2E5BFF" },
  { id: "bleu-clair", label: { fr: "Bleu clair" },  hex: "#00E5FF" },
  { id: "rouge",      label: { fr: "Rouge" },       hex: "#FF1F2D" },
  { id: "orange",     label: { fr: "Orange" },      hex: "#FF7A00" },
  { id: "jaune",      label: { fr: "Jaune" },       hex: "#FFE600" },
  { id: "blanc",      label: { fr: "Blanc" },       hex: "#FFFFFF" },
  { id: "magenta",    label: { fr: "Magenta" },     hex: "#FF1FA8" },
];
const COLORS = CROSS_COLORS;

// Familles de croix — designs extraits directement de vos 5 PDFs individuels (c1..c5)
// `hasArrow` : indique que le modèle contient une ou plusieurs flèches → 2e color-picker exposé
const SCENES = CROSS_SCENES;
// IDs des croix personnalisables (anciennement appliqué aux PNGs).
// La famille "Croix" est désormais composée de :
//  - 22 nouvelles croix personnalisables (SVG multi-couches avec option "Transparent" par couche),
//    issues du fichier `nouvelle-croix.svg` (manifest croix-v3).
//  - Les anciennes croix preset (PNG) marquées comme effets (couleurs imposées).
// Index PNG considérés comme "effets" : 6, 7, 8, 10, 11, 12, 14, 15, 16, 17, 18, 20, 22.
const EFFET_CROIX_INDEXES = [6, 7, 8, 10, 11, 12, 14, 15, 16, 17, 18, 20, 22];

// ─── Dot-Matrix designs (croix point-à-point) ────────────────────────
// 2 designs spécifiques avec fond + LED colorables séparément. Slots :
//   - "bg"  : fond de la croix (gris foncé, vert pomme, vert foncé, blanc)
//   - "led" : couleur des points LED (vert, bleu, blanc)
// Le design 2 a en plus un slot "center" (carré central blanc plein).
const DOTMATRIX_DESIGNS = [
  // ─── DOUBLE FACE ───
  {
    id: "dotmatrix-df-contour",
    label: { fr: "Contour — Double face", de: "Kontur — Doppelseitig", en: "Outline — Double face", it: "Contorno — Doppia faccia" },
    family: "croix",
    face: "double",
    customizableSvg: true,
    svgPath: "/dotmatrix-svg/croix-dotmatrix-1.svg?v=6",
    viewBox: "0 0 617.532 527.005",
    customizable: true,
    isDotMatrix: true,
    colorLayers: [
      { id: "bg",  label: { fr: "Fond" }, default: "#2E2E2E", classes: ["bg"] },
      { id: "led", label: { fr: "LED" },  default: "#FFFFFF", classes: ["led"] },
    ],
  },
  {
    id: "dotmatrix-df-pleine",
    label: { fr: "Pleine — Double face", de: "Voll — Doppelseitig", en: "Full — Double face", it: "Piena — Doppia faccia" },
    family: "croix",
    face: "double",
    customizableSvg: true,
    svgPath: "/dotmatrix-svg/croix-dotmatrix-2.svg?v=6",
    viewBox: "0 0 617.532 527.005",
    customizable: true,
    isDotMatrix: true,
    colorLayers: [
      { id: "bg",  label: { fr: "Fond" }, default: "#2E2E2E", classes: ["bg"] },
      { id: "led", label: { fr: "LED" },  default: "#FFFFFF", classes: ["led"] },
    ],
  },
  // ─── SIMPLE FACE ───
  {
    id: "dotmatrix-sf-contour",
    label: { fr: "Contour — Simple face", de: "Kontur — Einseitig", en: "Outline — Single face", it: "Contorno — Faccia singola" },
    family: "croix",
    face: "simple",
    customizableSvg: true,
    svgPath: "/dotmatrix-svg/croix-dotmatrix-sf-1.svg?v=6",
    viewBox: "0 0 527.005 527.005",
    customizable: true,
    isDotMatrix: true,
    colorLayers: [
      { id: "bg",  label: { fr: "Fond" }, default: "#2E2E2E", classes: ["bg"] },
      { id: "led", label: { fr: "LED" },  default: "#FFFFFF", classes: ["led"] },
    ],
  },
  {
    id: "dotmatrix-sf-pleine",
    label: { fr: "Pleine — Simple face", de: "Voll — Einseitig", en: "Full — Single face", it: "Piena — Faccia singola" },
    family: "croix",
    face: "simple",
    customizableSvg: true,
    svgPath: "/dotmatrix-svg/croix-dotmatrix-sf-2.svg?v=6",
    viewBox: "0 0 532.46 527.005",
    customizable: true,
    isDotMatrix: true,
    colorLayers: [
      { id: "bg",  label: { fr: "Fond" }, default: "#2E2E2E", classes: ["bg"] },
      { id: "led", label: { fr: "LED" },  default: "#FFFFFF", classes: ["led"] },
    ],
  },
  // ─── INTÉRIEUR (Pleine uniquement, sans support de fixation) ───
  // Pour pose en pharmacie / vitrine intérieure : pas de potence, pas
  // d'orientation gauche/droite (la croix est posée à plat). Tailles
  // restreintes à 30, 50, 60 cm.
  {
    id: "dotmatrix-int-pleine",
    label: { fr: "Pleine — Intérieur", de: "Voll — Innen", en: "Full — Indoor", it: "Piena — Interno" },
    family: "croix",
    face: "interieur",
    customizableSvg: true,
    svgPath: "/dotmatrix-svg/croix-dotmatrix-sf-2.svg?v=6",
    viewBox: "0 0 532.46 527.005",
    customizable: true,
    isDotMatrix: true,
    colorLayers: [
      { id: "bg",  label: { fr: "Fond" }, default: "#2E2E2E", classes: ["bg"] },
      { id: "led", label: { fr: "LED" },  default: "#FFFFFF", classes: ["led"] },
    ],
  },
];

// Tailles autorisées pour les croix point-à-point selon le type de face.
// - Extérieur (simple/double face avec potence) : 60 ou 80 cm
// - Intérieur (sans potence, posé en pharmacie)  : 30, 50 ou 60 cm
const DOTMATRIX_SIZES_EXT = [60, 80];
const DOTMATRIX_SIZES_INT = [30, 50, 60];

// Palette de couleurs FOND (croix point-à-point uniquement) — gris foncé + vert
const DOTMATRIX_BG_COLORS = [
  { id: "gris-fonce",  label: { fr: "Gris foncé",  de: "Dunkelgrau", en: "Dark grey",   it: "Grigio scuro" }, hex: "#2E2E2E" },
  { id: "vert-fonce",  label: { fr: "Vert foncé",  de: "Dunkelgrün", en: "Dark green",  it: "Verde scuro" },  hex: "#0f5f2a" },
];

// Palette de couleurs LED (point-à-point) — vert / bleu
const DOTMATRIX_LED_COLORS = [
  { id: "vert",  label: { fr: "Vert",  de: "Grün",  en: "Green", it: "Verde" }, hex: "#39FF14" },
  { id: "bleu",  label: { fr: "Bleu",  de: "Blau",  en: "Blue",  it: "Blu" },   hex: "#00B8FF" },
];

const CROIX_V3_SPRITE = CROIX_V3_MANIFEST.spritePath;
const CROIX_SIMPLE_SPRITE = CROIX_SIMPLE_MANIFEST.spritePath;

// New simplified crosses (12 total : 2 rows of 6). Row 1 = single-color,
// row 2 = main color + interior color (with transparent option).
// They REPLACE the previous Croix 1-6 from the V3 manifest.
const CUSTOMIZABLE_CROIX_DESIGNS_SIMPLE = CROIX_SIMPLE_MANIFEST.designs.map((d) => ({
  id: d.id,
  label: { fr: `Croix ${d.idx}` },
  family: "croix",
  customizableSvg: true,
  svgPath: CROIX_SIMPLE_SPRITE,
  viewBox: d.viewBox,
  customizable: true,
  colorLayers: d.slots.map((s) => ({
    id: s.id,
    label: s.label,
    default: s.default,
    targetIds: s.targetIds,
    allowTransparent: !!s.allowTransparent,
    synthetic: s.synthetic || null,
  })),
}));

// Existing V3 customizable crosses, EXCLUDING the first 6 (replaced by the
// new simplified set above). Re-numbered to follow the simple set.
const CUSTOMIZABLE_CROIX_DESIGNS_V3 = CROIX_V3_MANIFEST.designs.slice(6).map((d, i) => ({
  id: d.id,
  label: { fr: `Croix ${CUSTOMIZABLE_CROIX_DESIGNS_SIMPLE.length + i + 1}` },
  family: "croix",
  customizableSvg: true,
  svgPath: CROIX_V3_SPRITE,
  viewBox: d.viewBox,
  customizable: true,
  colorLayers: d.slots.map((s) => ({
    id: s.id,
    label: s.label,
    default: s.default,
    targetIds: s.targetIds,
    allowTransparent: !!s.allowTransparent,
    synthetic: s.synthetic || null,
  })),
}));

const CUSTOMIZABLE_CROIX_DESIGNS = [
  ...CUSTOMIZABLE_CROIX_DESIGNS_SIMPLE,
  ...CUSTOMIZABLE_CROIX_DESIGNS_V3,
];

// === LEGACY CROIX (croix-neon-led page) ===
// 5 nouvelles croix vectorielles (PDF → SVG via PyMuPDF) avec traits
// recolorables individuellement. Chaque trait (path) du SVG porte un id
// `trait-N` injecté par `/app/scripts/build_croix_neon_manifest.py` ; côté
// frontend, on expose un slot par trait et `CustomizableSvg.idOverrides`
// applique la couleur sélectionnée via une règle CSS injectée.
// Version de cache pour forcer le rechargement des SVG néon après chaque
// régénération des PDF → SVG (évite les soucis de cache navigateur).
const NEON_SVG_VERSION = "20260216j";

// Regroupements personnalisés pour les Croix néon — chaque entrée mappe un
// slot vers une liste de trait-ids (mécanisme identique à RONDE_NEON_GROUPS).
const CROIX_NEON_GROUPS = {
  "croix-neon-8": [
    { id: "groupe-1", label: { fr: "Groupe 1 (traits 1-4)" }, targetIds: ["trait-1", "trait-2", "trait-3", "trait-4"] },
    { id: "groupe-2", label: { fr: "Groupe 2 (traits 5-8)" }, targetIds: ["trait-5", "trait-6", "trait-7", "trait-8"] },
  ],
};
export const LEGACY_CROIX_DESIGNS = CROIX_NEON_MANIFEST.designs.map((d) => {
  const customGroups = CROIX_NEON_GROUPS[d.id];
  const colorLayers = customGroups
    ? customGroups.map((g) => ({ ...g, default: "#22FF00" }))
    : Array.from({ length: d.traitCount }, (_, i) => ({
        id: `trait-${i + 1}`,
        label: { fr: `Trait ${i + 1}` },
        default: "#22FF00", // couleur d'origine du SVG (vert clair néon)
        targetIds: [`trait-${i + 1}`],
      }));
  return {
    id: d.id,
    label: d.label,
    family: "croix",
    customizableSvg: true,
    svgPath: `${d.svgPath}?v=${NEON_SVG_VERSION}`,
    viewBox: d.viewBox,
    customizable: true,
    colorLayers,
  };
});

// === LEGACY RONDES (croix-neon-led "Ronde / Médaillon") ===
// 5 designs ronde/médaillon vectoriels (PDF → SVG via PyMuPDF) avec traits
// recolorables individuellement, normalisés sur le même vert clair néon #22FF00.
// Regroupements personnalisés (utilisateur) — chaque entrée mappe un slot vers
// une liste de trait-ids. Si pas de regroupement, on garde 1 slot par trait.
const RONDE_NEON_GROUPS = {
  "ronde-neon-2": [
    { id: "trait-1", label: { fr: "Trait 1" }, targetIds: ["trait-1"] },
    { id: "trait-2", label: { fr: "Trait 2" }, targetIds: ["trait-2"] },
    { id: "groupe-1", label: { fr: "Groupe 1 (traits 3-6)" }, targetIds: ["trait-3", "trait-4", "trait-5", "trait-6"] },
    { id: "groupe-2", label: { fr: "Groupe 2 (traits 7-10)" }, targetIds: ["trait-7", "trait-8", "trait-9", "trait-10"] },
  ],
};
export const LEGACY_RONDE_DESIGNS = RONDE_NEON_MANIFEST.designs.map((d) => {
  const customGroups = RONDE_NEON_GROUPS[d.id];
  const colorLayers = customGroups
    ? customGroups.map((g) => ({ ...g, default: "#22FF00" }))
    : Array.from({ length: d.traitCount }, (_, i) => ({
        id: `trait-${i + 1}`,
        label: { fr: `Trait ${i + 1}` },
        default: "#22FF00",
        targetIds: [`trait-${i + 1}`],
      }));
  return {
    id: d.id,
    label: d.label,
    family: "ronde",
    customizableSvg: true,
    svgPath: `${d.svgPath}?v=${NEON_SVG_VERSION}`,
    viewBox: d.viewBox,
    customizable: true,
    colorLayers,
  };
});

const EFFET_CROIX_DESIGNS = EFFET_CROIX_INDEXES.map((idx) => ({
  id: `classic-${idx}`,
  label: { fr: `Effet ${idx}` },
  family: "croix",
  imgColor: `/crosses-classic/classic-${idx}.png`,
  preset: true,
  customizable: false,
}));

// Croix de pharmacie — personnalisables d'abord, puis effets
export const CROIX_DESIGNS = [...CUSTOMIZABLE_CROIX_DESIGNS, ...EFFET_CROIX_DESIGNS];

// Croix avec flèche — 2 modèles principaux (corps + flèche customizables) + 5 presets multicolore
export const CROIX_FLECHE_DESIGNS = [
  { id: "croix-fleche-1", label: { fr: "Croix Flèche 1" }, family: "croix-fleche", imgColor: "/crosses/croix-fleche-main-1-color.png", imgBody: "/crosses/croix-fleche-main-1-body.png", imgArrow: "/crosses/croix-fleche-main-1-arrow.png", hasArrow: true, customizable: true },
  { id: "croix-fleche-2", label: { fr: "Croix Flèche 2" }, family: "croix-fleche", imgColor: "/crosses/croix-fleche-main-2-color.png", imgBody: "/crosses/croix-fleche-main-2-body.png", imgArrow: "/crosses/croix-fleche-main-2-arrow.png", hasArrow: true, customizable: true },
  // 6 designs preset multicolore
  ...Array.from({ length: 6 }, (_, i) => ({
    id: `fleche-preset-${i + 1}`,
    label: { fr: `Flèche Multi ${i + 1}` },
    family: "croix-fleche",
    imgColor: `/crosses/fleche-preset-${i + 1}-color.png`,
    preset: true,
  })),
];

// Designs ronde — 20 designs SVG customisables (V2 - fichier "ronde simplifier 2")
// Disposés en 5 rangées × 4 colonnes. Chaque design a son propre jeu de slots
// (2 à 5 couleurs personnalisables selon la rangée).
const RONDE_V2_SPRITE = RONDES_V2_MANIFEST.spritePath;
export const RONDE_DESIGNS = RONDES_V2_MANIFEST.designs.map((d) => ({
  id: d.id,
  label: { fr: `Ronde ${d.idx}` },
  family: "ronde",
  customizableSvg: true,
  svgPath: RONDE_V2_SPRITE,
  viewBox: d.viewBox,
  // Each layer drives an inline-style fill override on the listed SVG element IDs.
  colorLayers: d.slots.map((s) => ({
    id: s.id,
    label: s.label,
    default: s.default,
    targetIds: s.targetIds,
  })),
}));

// Designs multi-couleur — couleurs déjà imposées par le design, pas de color-picker
export const MULTI_DESIGNS = [
  { id: "multi-model-1", label: { fr: "Multi 1" }, family: "multi", imgColor: "/crosses/multi-model-1-color.png" },
  { id: "multi-model-2", label: { fr: "Multi 2" }, family: "multi", imgColor: "/crosses/multi-model-2-color.png" },
  { id: "multi-model-3", label: { fr: "Multi 3" }, family: "multi", imgColor: "/crosses/multi-model-3-color.png" },
  { id: "multi-model-4", label: { fr: "Multi 4" }, family: "multi", imgColor: "/crosses/multi-model-4-color.png" },
  { id: "multi-model-5", label: { fr: "Multi 5" }, family: "multi", imgColor: "/crosses/multi-model-5-color.png" },
  { id: "multi-model-6", label: { fr: "Multi 6" }, family: "multi", imgColor: "/crosses/multi-model-6-color.png" },
  { id: "multi-model-7", label: { fr: "Multi 7" }, family: "multi", imgColor: "/crosses/multi-model-7-color.png" },
  { id: "multi-model-8", label: { fr: "Multi 8" }, family: "multi", imgColor: "/crosses/multi-model-8-color.png" },
  { id: "multi-model-9", label: { fr: "Multi 9" }, family: "multi", imgColor: "/crosses/multi-model-9-color.png" },
  { id: "multi-model-10", label: { fr: "Multi 10" }, family: "multi", imgColor: "/crosses/multi-model-10-color.png" },
  { id: "multi-model-11", label: { fr: "Multi 11" }, family: "multi", imgColor: "/crosses/multi-model-11-color.png" },
  { id: "multi-model-12", label: { fr: "Multi 12" }, family: "multi", imgColor: "/crosses/multi-model-12-color.png" },
  { id: "multi-model-13", label: { fr: "Multi 13" }, family: "multi", imgColor: "/crosses/multi-model-13-color.png" },
  { id: "multi-model-14", label: { fr: "Multi 14" }, family: "multi", imgColor: "/crosses/multi-model-14-color.png" },
  { id: "multi-model-15", label: { fr: "Multi 15" }, family: "multi", imgColor: "/crosses/multi-model-15-color.png" },
  { id: "multi-model-16", label: { fr: "Multi 16" }, family: "multi", imgColor: "/crosses/multi-model-16-color.png" },
  { id: "multi-model-17", label: { fr: "Multi 17" }, family: "multi", imgColor: "/crosses/multi-model-17-color.png" },
  { id: "multi-model-18", label: { fr: "Multi 18" }, family: "multi", imgColor: "/crosses/multi-model-18-color.png" },
  { id: "multi-model-19", label: { fr: "Multi 19" }, family: "multi", imgColor: "/crosses/multi-model-19-color.png" },
  { id: "multi-model-20", label: { fr: "Multi 20" }, family: "multi", imgColor: "/crosses/multi-model-20-color.png" },
  { id: "multi-model-21", label: { fr: "Multi 21" }, family: "multi", imgColor: "/crosses/multi-model-21-color.png" },
];

// Designs ronde multi-couleur — médaillons multicolores, couleurs imposées
export const RONDE_MULTI_DESIGNS = Array.from({ length: 25 }, (_, i) => ({
  id: `ronde-multi-${i + 1}`,
  label: { fr: `Médaillon Multi ${i + 1}` },
  family: "ronde-multi",
  imgColor: `/crosses/ronde-multi-${i + 1}-color.png`,
}));

// Enseigne d'angle — 5 designs avec 2 couches (intérieur + contour) coloriables séparément
export const ANGLE_DESIGNS = Array.from({ length: 5 }, (_, i) => ({
  id: `angle-${i + 1}`,
  label: { fr: `Angle ${i + 1}` },
  family: "angle",
  imgColor: `/angles/angle-${i + 1}-color.png`,
  imgBody: `/angles/angle-${i + 1}-layer1.png`,
  imgArrow: `/angles/angle-${i + 1}-layer2.png`,
  hasArrow: true,
  customizable: true,
}));

export const ALL_CROSS_DESIGNS = [...DOTMATRIX_DESIGNS, ...CROIX_DESIGNS, ...LEGACY_CROIX_DESIGNS, ...CROIX_FLECHE_DESIGNS, ...RONDE_DESIGNS, ...LEGACY_RONDE_DESIGNS, ...MULTI_DESIGNS, ...RONDE_MULTI_DESIGNS, ...ANGLE_DESIGNS];
const ALL_DESIGNS = ALL_CROSS_DESIGNS;
// Registre global runtime — permet à CompositionOverlay d'accéder aux designs
// sans créer d'import circulaire avec CrossConfigurator (qui consomme déjà
// CompositionOverlay). Sûr : ce module se charge dès la 1ère visite d'une
// page utilisant CrossConfigurator, ce qui précède toujours un overlay
// pour des fantômes de croix (l'utilisateur a forcément visité la page croix
// pour créer la configuration en question).
if (typeof window !== "undefined") {
  window.__SIGNAPHARMA_CROSS_DESIGNS__ = ALL_DESIGNS;
}

// Helpers pour la composition multi-éléments
const FIRST_DESIGN_OF_FAMILY = {
  croix: "croix-simple-1",
  "croix-fleche": "croix-fleche-1",
  ronde: "ronde-v2-1",
  multi: "multi-model-1",
  "ronde-multi": "ronde-multi-1",
  angle: "angle-1",
  classic: "croix-simple-1",
};

const makeItem = (fam = "croix", overrides = {}) => ({
  id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  family: fam,
  designId: FIRST_DESIGN_OF_FAMILY[fam] || "croix-simple-1",
  colorId: "vert",
  arrowColorId: "blanc",
  sizeCm: 50,
  pos: { x: 50, y: 50 },
  scale: 1,
  rotation: 0,
  customColors: {}, // overrides par classe CSS pour les designs customizableSvg
  tintColor: null,  // couleur unique appliquée sur les designs PNG (mask-image) ; null = couleur d'origine
  face: "simple",   // "simple" (montage à plat) | "double" (avec potence murale, double-face)
  potenceRotation: 0, // angle (degrés) du bras de potence autour de la croix (0=droite, 90=bas, 180=gauche, 270=haut)
  ...overrides,
});

const STORAGE_KEY_BASE = "signapharma-cross-composition";

export default function CrossConfigurator({ value, onChange, initialFamily, productSlug }) {
  const { lang } = useI18n();
  const { reset: resetComposition } = useComposition();
  const previewRef = useRef(null);
  const dragRef = useRef({ active: false, mode: "move", startY: 0, startScale: 1, itemId: null });
  // En mode "classic" (page croix-classique-led), on n'affiche que les onglets Croix + Ronde.
  // La famille par défaut est "croix" pour correspondre au premier onglet visible.
  const isSimplifiedMode = initialFamily === "classic";
  const isDotMatrixMode = initialFamily === "dotmatrix";
  const defaultFamily = isSimplifiedMode || isDotMatrixMode ? "croix" : (initialFamily || "croix");
  // Clé localStorage séparée par famille pour éviter que les pages classique/néon se croisent
  const STORAGE_KEY = `${STORAGE_KEY_BASE}-${initialFamily || "any"}`;

  // ---------- Composition multi-éléments ----------
  const [items, setItems] = useState(() => {
    // 1) Restore from explicit prop
    if (value?.items?.length) return value.items;
    // 2) Restore from localStorage (auto-save)
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (stored?.items?.length && stored.initialFamily === (initialFamily || "any")) return stored.items;
    } catch (e) { /* ignore */ }
    // 3) Default single item — sentinel "_empty_" : aucune croix visible
    //    par défaut. L'utilisateur DOIT choisir un design dans la grille
    //    pour qu'une croix s'affiche.
    return [makeItem(defaultFamily, { designId: `_empty_${Math.random().toString(36).slice(2, 7)}` })];
  });
  const [activeId, setActiveId] = useState(() => items[0]?.id);
  const activeIndex = Math.max(0, items.findIndex((it) => it.id === activeId));
  const active = items[activeIndex] || items[0];

  // Query param ?design=<id> : préselection depuis la barre de recherche.
  // On applique une seule fois si le design existe dans le registry ET
  // correspond à une famille supportée par cette page.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get("design");
    if (!target) return;
    const design = ALL_DESIGNS.find((d) => d.id === target);
    if (!design) return;
    setItems((prev) => prev.map((it, i) => (i === 0
      ? { ...it, family: design.family, designId: target }
      : it)));
    setActiveId((prev) => items[0]?.id || prev);
    // Retire le param pour éviter re-application en cas de re-render
    const next = new URLSearchParams(searchParams);
    next.delete("design");
    setSearchParams(next, { replace: true });
  }, []);

  const patchActive = (patch) => setItems((prev) => prev.map((it, i) => (i === activeIndex ? { ...it, ...patch } : it)));

  // ---------- Pricing temps réel ----------
  // Le calcul est délégué à <GlobalPricePanel /> qui affiche TOUS les
  // éléments configurés (croix + lettres) sur toutes les pages.

  // Proxies (les composants/handlers existants utilisent ces variables comme avant)
  const family = active.family;
  const setFamily = (v) => patchActive({ family: v, designId: FIRST_DESIGN_OF_FAMILY[v] || "croix-simple-1" });
  const designId = active.designId;
  const setDesignId = (v) => {
    // En mode point-à-point, on auto-corrige la taille et l'orientation
    // pour rester cohérents avec le type de croix choisi (extérieur/intérieur).
    if (isDotMatrixMode) {
      const nextDesign = DOTMATRIX_DESIGNS.find((d) => d.id === v);
      if (nextDesign) {
        const isInterior = nextDesign.face === "interieur";
        const allowedSizes = isInterior ? DOTMATRIX_SIZES_INT : DOTMATRIX_SIZES_EXT;
        const patch = { designId: v };
        if (!allowedSizes.includes(active.sizeCm)) {
          // Choisit 60 cm si dispo (commun aux 2 gammes), sinon la 1ère taille.
          patch.sizeCm = allowedSizes.includes(60) ? 60 : allowedSizes[0];
        }
        if (isInterior) {
          // Pas de potence en intérieur — on remet la rotation à 0 (neutre).
          patch.rotation = 0;
        } else if (![90, 270].includes(active.rotation)) {
          // En extérieur on n'autorise plus que Gauche/Droite.
          patch.rotation = 90;
        }
        patchActive(patch);
        return;
      }
    }
    patchActive({ designId: v });
  };
  const colorId = active.colorId;
  const setColorId = (v) => patchActive({ colorId: v });
  const arrowColorId = active.arrowColorId;
  const setArrowColorId = (v) => patchActive({ arrowColorId: v });
  const customColors = active.customColors || {};
  const setCustomColor = (layerId, hex) => patchActive({ customColors: { ...(active.customColors || {}), [layerId]: hex } });
  // Applique une couleur unique à tous les slots customColors d'un design néon
  // (utilisé par la barre pilule horizontale pour Croix néon / Ronde médaillon).
  const setAllCustomColors = (hex) => {
    if (!design?.colorLayers) return;
    const next = {};
    for (const layer of design.colorLayers) next[layer.id] = hex;
    patchActive({ customColors: next });
  };
  const resetCustomColors = () => patchActive({ customColors: {} });
  const tintColor = active.tintColor;
  const setTintColor = (hex) => patchActive({ tintColor: hex });
  const sizeCm = active.sizeCm;
  const setSizeCm = (v) => patchActive({ sizeCm: v });
  const pos = active.pos;
  const setPos = (v) => patchActive({ pos: typeof v === "function" ? v(active.pos) : v });
  const scale = active.scale;
  const setScale = (v) => patchActive({ scale: typeof v === "function" ? v(active.scale) : v });
  const rotation = active.rotation;
  const setRotation = (v) => patchActive({ rotation: typeof v === "function" ? v(active.rotation) : v });

  // Params partagés (s'appliquent à toute la composition)
  const [sceneId, setSceneId] = useState(value?.sceneId || "facade-modern-white");
  const [customScene, setCustomScene] = useState(value?.customScene || null);
  const [mode, setMode] = useState(value?.mode || "day");
  const [interactionMode, setInteractionMode] = useState("move");

  // Mobile : force la scène "bardage blanc" et masque le sélecteur de scènes
  const isMobile = useIsMobile();
  const effectiveSceneId = isMobile ? "white-bardage" : sceneId;
  const effectiveCustomScene = isMobile ? null : customScene;

  // Auto-save localStorage
  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, initialFamily: initialFamily || "any" }));
    } catch (e) { /* ignore */ }
  }, [items, initialFamily]);

  // Actions composition
  const addItem = () => {
    const next = makeItem(family, { colorId, arrowColorId, sizeCm, scale, pos: { x: Math.min(85, pos.x + 12), y: pos.y }, rotation });
    setItems((prev) => [...prev, next]);
    setActiveId(next.id);
  };
  const removeItem = (id) => {
    setItems((prev) => {
      const filtered = prev.filter((it) => it.id !== id);
      // Si l'utilisateur supprime le DERNIER élément, on remplace par un
      // calque vierge "_empty_" : la croix disparaît du preview, mais le
      // configurateur reste fonctionnel (l'utilisateur peut choisir un
      // nouveau design dans la grille pour réafficher une croix).
      if (filtered.length === 0) {
        const fresh = makeItem(defaultFamily, { designId: `_empty_${Math.random().toString(36).slice(2, 7)}` });
        setTimeout(() => setActiveId(fresh.id), 0);
        return [fresh];
      }
      if (id === activeId) setTimeout(() => setActiveId(filtered[0].id), 0);
      return filtered;
    });
  };
  // Touche Suppr / Backspace : supprime l'item actif. Pas de garde-fou sur
  // items.length — l'appel à removeItem ci-dessus se charge de réinitialiser
  // proprement si on supprime le dernier élément (= efface toutes les persos).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const t = e.target;
      const tag = (t?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
      if (!activeId) return;
      e.preventDefault();
      removeItem(activeId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);
  const duplicateItem = (id) => {
    const src = items.find((it) => it.id === id);
    if (!src) return;
    const copy = { ...src, id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, pos: { x: Math.min(85, src.pos.x + 8), y: Math.min(85, src.pos.y + 8) } };
    setItems((prev) => [...prev, copy]);
    setActiveId(copy.id);
  };

  // Si l'utilisateur a choisi la case "Aucun" (`_empty_<family>`), on garde
  // `_designPick` pointé sur l'EMPTY_DESIGN local — la preview détectera ce
  // sentinel via `design._empty` pour cacher la croix sur la façade. Tous
  // les panneaux de personnalisation (couleurs, palette, traits) sont aussi
  // masqués pour éviter d'éditer une croix invisible.
  const isEmptyPick = !!(designId && String(designId).startsWith("_empty_"));
  const _designPick = isEmptyPick
    ? { id: designId, family, _empty: true, label: { fr: "Aucun" } }
    : (ALL_DESIGNS.find((d) => d.id === designId) || CROIX_DESIGNS[0]);
  // Un design "customizableSvg" expose son propre module de personnalisation
  // (palette multi-couches), il ne doit ni être traité comme preset, ni montrer
  // les color pickers standards (qui ne s'appliqueraient à rien).
  const isCustomizableSvg = !!_designPick.customizableSvg;
  // "preset" = design figé (couleurs imposées) ; le module de teinte (`tintColor`)
  // est exposé uniquement pour les designs preset *ET* marqués `customizable` côté
  // famille Croix (sous-onglet "Personnalisable"). Les croix de l'onglet "Effet"
  // sont volontairement non-modifiables.
  const isPresetDesign = isCustomizableSvg || (family === "multi") || (family === "ronde-multi") || (family === "classic") || ((family === "croix" || family === "ronde") && _designPick.preset) || (family === "croix-fleche" && _designPick.preset);
  // La barre de teinte globale "Couleur de la croix" est cachée pour :
  //  - Multi-couleur & Ronde Multi-couleur (designs preset multicolores fixés)
  //  - Croix Flèche Multi 1-6 (presets multicolores idem — pas de repeinte
  //    globale, le design impose ses couleurs)
  //  - Designs marqués `customizable: false`
  //  - Case vide (rien à personnaliser)
  const allowTintPicker = !_designPick._empty && isPresetDesign && !isCustomizableSvg && family !== "multi" && family !== "ronde-multi" && family !== "croix-fleche" && !(family === "croix" && _designPick.customizable === false);
  const color = COLORS.find((c) => c.id === colorId) || COLORS[0];
  const design = _designPick;
  // Palette restreinte (8 nuances néon) pour les designs "Croix néon LED" et
  // "Ronde médaillon néon" (ids `croix-neon-N` / `ronde-neon-N`). Les autres
  // designs gardent la palette complète CUSTOM_PALETTE.
  // Mode point-à-point : palette restreinte par slot :
  //   • slot `bg`     → 4 fonds (gris foncé / vert pomme / vert foncé / blanc)
  //   • slot `led`    → 3 couleurs LED (vert / bleu / blanc)
  //   • slot `center` → mêmes 4 fonds que `bg`
  const isNeonDesign = !!(design?.id && (design.id.startsWith("croix-neon-") || design.id.startsWith("ronde-neon-")));
  const isDotMatrix = !!design?.isDotMatrix;
  const dotMatrixPaletteForLayer = (layerId) =>
    layerId === "led" ? DOTMATRIX_LED_COLORS : DOTMATRIX_BG_COLORS;
  const activePalette = isNeonDesign
    ? NEON_PALETTE
    : isDotMatrix
      ? null /* per-layer palette ; cf. dotMatrixPaletteForLayer */
      : CUSTOM_PALETTE;
  const scene = effectiveCustomScene
    ? { id: "custom", label: { fr: "Ma photo" }, image: effectiveCustomScene }
    : SCENES.find((s) => s.id === effectiveSceneId) || SCENES[0];

  const sceneFile = useRef(null);

  // Liste affichée selon famille — sur la page `croix-classique-led`
  // (isSimplifiedMode), on utilise les nouvelles séries V3+Simple ; sur la
  // page `croix-neon-led`, on utilise les originaux "en trait" (legacy).
  let rawDesignsList;
  if (isDotMatrixMode) rawDesignsList = DOTMATRIX_DESIGNS;
  else if (family === "angle") rawDesignsList = ANGLE_DESIGNS;
  else if (family === "ronde-multi") rawDesignsList = RONDE_MULTI_DESIGNS;
  else if (family === "multi") rawDesignsList = MULTI_DESIGNS;
  else if (family === "croix-fleche") rawDesignsList = CROIX_FLECHE_DESIGNS;
  else if (family === "ronde") rawDesignsList = isSimplifiedMode ? RONDE_DESIGNS : LEGACY_RONDE_DESIGNS;
  else rawDesignsList = isSimplifiedMode ? CROIX_DESIGNS : LEGACY_CROIX_DESIGNS;

  // Sentinel "case vide" — ajouté en 1ère position de chaque grille de
  // designs (toutes familles). Cliquer dessus cache la croix sur la
  // façade pour permettre une scène propre / un montage multi-catégories
  // sans imposer une croix par défaut.
  const EMPTY_DESIGN = useMemo(() => ({
    id: `_empty_${family}`,
    label: { fr: "Aucun", de: "Keine", en: "None", it: "Nessuno" },
    family,
    _empty: true,
  }), [family]);
  const designsList = useMemo(() => [EMPTY_DESIGN, ...rawDesignsList], [EMPTY_DESIGN, rawDesignsList]);

  // userTouchedRef : démarre TOUJOURS à `false` au montage (même si la page
  // a déjà été configurée auparavant). Sinon : revenir sur une page validée
  // déclencherait une re-propagation à chaque rendu et empilerait des
  // fantômes / doublons sur les autres pages. Le state existant reste
  // chargé depuis le contexte via `value`, mais ne sera re-propagé qu'après
  // une interaction explicite.
  const userTouchedRef = useRef(false);
  const isTypingTarget = (target) => {
    if (!target) return false;
    const tag = (target.tagName || "").toUpperCase();
    if (tag === "TEXTAREA") return true;
    if (tag === "INPUT") {
      const type = (target.type || "text").toLowerCase();
      return type === "text" || type === "search" || type === "email" || type === "tel" || type === "password";
    }
    return false;
  };
  const markTouched = (e) => {
    if (e && isTypingTarget(e.target)) return;
    userTouchedRef.current = true;
  };

  React.useEffect(() => {
    if (!userTouchedRef.current) return;
    // Push composition complète au parent (cart/devis)
    onChange?.({
      items,
      activeId,
      sceneId: effectiveCustomScene ? "custom" : effectiveSceneId,
      customScene: effectiveCustomScene,
      mode,
      _touched: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, activeId, sceneId, customScene, mode]);

  // Écoute un événement global `signapharma:capture-config` dispatché juste
  // avant l'ajout au panier. Force la remontée de l'état COURANT du
  // configurateur même si l'utilisateur n'a pas touché à la preview
  // (cas : il clique direct "Ajouter au panier" sur les valeurs par défaut).
  // Écrit aussi la config dans `window._signapharmaConfigCapture[slug]`
  // pour permettre une lecture synchrone (zero-latence) côté caller.
  React.useEffect(() => {
    const handler = () => {
      const cfg = {
        items,
        activeId,
        sceneId: effectiveCustomScene ? "custom" : effectiveSceneId,
        customScene: effectiveCustomScene,
        mode,
        _touched: true,
      };
      onChange?.(cfg);
      if (productSlug) {
        window._signapharmaConfigCapture = window._signapharmaConfigCapture || {};
        window._signapharmaConfigCapture[productSlug] = cfg;
      }
    };
    window.addEventListener("signapharma:capture-config", handler);
    return () => window.removeEventListener("signapharma:capture-config", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, activeId, effectiveSceneId, effectiveCustomScene, mode, productSlug]);

  // ---------- drag ----------
  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = {
      active: true, mode: interactionMode,
      startY: (e.touches?.[0]?.clientY ?? e.clientY) || 0,
      startScale: scale,
    };
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d.active || !previewRef.current) return;
    const pt = e.touches?.[0] || e;
    if (d.mode === "move") {
      const rect = previewRef.current.getBoundingClientRect();
      const x = ((pt.clientX - rect.left) / rect.width) * 100;
      const y = ((pt.clientY - rect.top) / rect.height) * 100;
      setPos({ x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) });
    } else {
      const delta = d.startY - pt.clientY;
      setScale(Math.max(0.4, Math.min(2.5, d.startScale + delta / 150)));
    }
  };
  const endDrag = () => { dragRef.current.active = false; };

  // ---------- upload ----------
  const onUploadScene = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = (ev) => setCustomScene(ev.target.result); r.readAsDataURL(f);
  };

  // ---------- preview render ----------
  // Taille en px = sizeCm * PX_PER_CM * scale
  // PX_PER_CM divisé par 1.5 (de 2.5 → 1.667) pour un rendu plus proportionnel
  // à la façade réelle (demande utilisateur).
  const CROSS_PX_PER_CM = 2.5 / 1.5;
  const previewSizePx = useMemo(() => sizeCm * CROSS_PX_PER_CM * scale, [sizeCm, scale, CROSS_PX_PER_CM]);

  const neonGlowFilter = useMemo(() => {
    if (color.isMulti) {
      const lvl = mode === "night" ? 0.8 : 0.5;
      return `drop-shadow(0 0 ${5*lvl}px rgba(255,0,0,${0.85*lvl})) drop-shadow(0 0 ${14*lvl}px rgba(0,255,0,${0.7*lvl})) drop-shadow(0 0 ${28*lvl}px rgba(0,120,255,${0.65*lvl})) drop-shadow(0 0 ${48*lvl}px rgba(180,0,255,${0.5*lvl}))`;
    }
    if (colorId === "blanc" && mode === "day") {
      return `drop-shadow(0 0 1.5px rgba(0,0,0,0.9)) drop-shadow(0 0 3px rgba(0,0,0,0.6)) drop-shadow(0 0 10px rgba(255,255,255,0.9)) drop-shadow(0 0 24px rgba(200,220,255,0.65)) drop-shadow(0 0 50px rgba(150,200,255,0.5))`;
    }
    const c = color.hex;
    const lvl = mode === "night" ? 0.9 : 0.55;
    // Core blanc + 3 couches de halo coloré pour effet LED haute intensité
    return `drop-shadow(0 0 ${2*lvl}px #fff) drop-shadow(0 0 ${5*lvl}px #fff) drop-shadow(0 0 ${12*lvl}px ${c}) drop-shadow(0 0 ${24*lvl}px ${c}) drop-shadow(0 0 ${48*lvl}px ${c}) drop-shadow(0 0 ${72*lvl}px ${c})`;
  }, [color, colorId, mode]);

  const strokeColor = color.isMulti ? "#fff" : color.hex;
  const fillColor = color.isMulti ? "#fff" : color.hex;
  const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-3 flex items-center gap-2";

  return (
    <div
      className="space-y-6"
      data-testid="cross-configurator"
      onClickCapture={markTouched}
      onPointerDownCapture={markTouched}
    >
      <MobileDesktopHint />
      <ProjectsToolbar />

      {/* ===== Prix temps réel (CHF HT) — affiche TOUS les éléments
          configurés sur toutes les pages (croix + lettres) avec un bouton
          "Ajouter au panier" par ligne. Pour la page courante on passe
          l'état local (items + scène) pour éviter le lag d'un tick. */}
      <GlobalPricePanel
        currentSlug={productSlug}
        localCfg={{ items, sceneId: effectiveSceneId, customScene: effectiveCustomScene, mode }}
      />

      {/* Notice intérieur */}
      <div className="border border-[#39FF14]/30 bg-[#39FF14]/[0.04] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14] flex items-center gap-2">
        <Sun size={12} />
        {lang === "fr" ? "Modèle d'intérieur uniquement — pour pose en pharmacie" : "Indoor model only — for in-store mounting"}
      </div>

      {/* Day/Night */}
      <div className="flex items-center gap-0 border border-white/10 bg-[#070707]">
        <div className="px-4 py-3 border-r border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{lang === "fr" ? "Ambiance" : "Mood"}</div>
        </div>
        <button type="button" onClick={() => setMode("day")} data-testid="mode-day"
          className={`flex-1 flex items-center justify-center gap-2 py-3 font-mono text-xs uppercase tracking-widest ${mode === "day" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
          <Sun size={14} />{lang === "fr" ? "Jour" : "Day"}
        </button>
        <button type="button" onClick={() => setMode("night")} data-testid="mode-night"
          className={`flex-1 flex items-center justify-center gap-2 py-3 font-mono text-xs uppercase tracking-widest ${mode === "night" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
          <Moon size={14} />{lang === "fr" ? "Nuit" : "Night"}
        </button>
      </div>

      {/* Preview + scene thumbnails */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_140px] gap-4">
        <div className="relative">
          <div className="mb-2 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{lang === "fr" ? "Outil" : "Tool"} :</span>
            <button type="button" onClick={() => setInteractionMode("move")} data-testid="tool-move"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${interactionMode === "move" ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400"}`}>
              <Move size={12} />{lang === "fr" ? "Déplacer" : "Move"}
            </button>
            <button type="button" onClick={() => setInteractionMode("resize")} data-testid="tool-resize"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${interactionMode === "resize" ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400"}`}>
              <Maximize2 size={12} />{lang === "fr" ? "Redimensionner" : "Resize"}
            </button>
            <div className="ml-auto flex items-center gap-2">
              <button type="button" onClick={addItem} data-testid="add-item"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10 font-mono text-[10px] uppercase tracking-widest hover:bg-[#39FF14]/20">
                <Plus size={12} />{lang === "fr" ? "Ajouter une croix" : "Add a cross"}
              </button>
              <button type="button" onClick={() => duplicateItem(activeId)} data-testid="duplicate-item"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 text-zinc-400 font-mono text-[10px] uppercase tracking-widest hover:border-white/30">
                <Copy size={12} />{lang === "fr" ? "Dupliquer" : "Duplicate"}
              </button>
              <button type="button" data-testid="reset-composition"
                onClick={() => resetComposition()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-500/50 text-red-400 font-mono text-[10px] uppercase tracking-widest hover:bg-red-500/10 hover:border-red-500"
                title={lang === "fr" ? "Vider tout le cache et recommencer" : "Wipe cache and start over"}>
                <RotateCcw size={12} />{lang === "fr" ? "Réinitialiser" : "Reset"}
              </button>
            </div>
          </div>

          <div ref={previewRef}
            onMouseMove={onMove} onMouseUp={endDrag} onMouseLeave={endDrag}
            onTouchMove={onMove} onTouchEnd={endDrag}
            className="relative aspect-[4/3] w-full overflow-hidden bg-[#040404] border border-white/10 select-none"
            data-testid="cross-preview">
            <img src={scene.image} alt="" draggable={false}
              data-snapshot-hide="true"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ objectPosition: "center bottom", filter: mode === "night" ? "brightness(0.32) saturate(0.85)" : "none" }} />
            {mode === "night" && <div data-snapshot-hide="true" className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(0,4,18,0.6), rgba(0,0,8,0.78))" }} />}

            {/* Overlay des éléments des AUTRES catégories (lecture seule) —
                permet de voir les lettres, néons, etc. sans quitter la page
                Croix. Un clic sur un fantôme renvoie vers son configurateur. */}
            <div data-snapshot-hide="true">
              <CompositionOverlay currentSlug={productSlug} mode={mode} />
            </div>

            {/* All items composition */}
            {items.map((it) => {
              // Si l'item a la "case vide" (`_empty_<family>`), on ne rend
              // rien sur la façade — l'utilisateur a explicitement décidé de
              // ne pas afficher d'élément ici.
              if (it.designId && String(it.designId).startsWith("_empty_")) return null;
              const isActive = it.id === activeId;
              const itDesign = ALL_DESIGNS.find((d) => d.id === it.designId) || CROIX_DESIGNS[0];
              const itIsPreset = (it.family === "multi") || (it.family === "ronde-multi") || (it.family === "classic") || ((it.family === "croix" || it.family === "ronde") && itDesign.preset) || (it.family === "croix-fleche" && itDesign.preset);
              const itColor = COLORS.find((c) => c.id === it.colorId) || COLORS[0];
              const itArrowColor = COLORS.find((c) => c.id === it.arrowColorId) || COLORS[0];
              const itSize = it.sizeCm * CROSS_PX_PER_CM * it.scale;
              const itGlow = (() => {
                if (it.colorId === "blanc" && mode === "day") {
                  return `drop-shadow(0 0 1.5px rgba(0,0,0,0.9)) drop-shadow(0 0 3px rgba(0,0,0,0.6)) drop-shadow(0 0 10px rgba(255,255,255,0.9)) drop-shadow(0 0 24px rgba(200,220,255,0.65)) drop-shadow(0 0 50px rgba(150,200,255,0.5))`;
                }
                const lvl = mode === "night" ? 0.9 : 0.55;
                return `drop-shadow(0 0 ${2*lvl}px #fff) drop-shadow(0 0 ${5*lvl}px #fff) drop-shadow(0 0 ${12*lvl}px ${itColor.hex}) drop-shadow(0 0 ${24*lvl}px ${itColor.hex}) drop-shadow(0 0 ${48*lvl}px ${itColor.hex}) drop-shadow(0 0 ${72*lvl}px ${itColor.hex})`;
              })();
              const itArrowGlow = (() => {
                if (it.arrowColorId === "blanc" && mode === "day") {
                  return `drop-shadow(0 0 1.5px rgba(0,0,0,0.9)) drop-shadow(0 0 3px rgba(0,0,0,0.6)) drop-shadow(0 0 10px rgba(255,255,255,0.9)) drop-shadow(0 0 24px rgba(200,220,255,0.65)) drop-shadow(0 0 50px rgba(150,200,255,0.5))`;
                }
                const lvl = mode === "night" ? 0.9 : 0.55;
                return `drop-shadow(0 0 ${2*lvl}px #fff) drop-shadow(0 0 ${5*lvl}px #fff) drop-shadow(0 0 ${12*lvl}px ${itArrowColor.hex}) drop-shadow(0 0 ${24*lvl}px ${itArrowColor.hex}) drop-shadow(0 0 ${48*lvl}px ${itArrowColor.hex}) drop-shadow(0 0 ${72*lvl}px ${itArrowColor.hex})`;
              })();
              return (
                <div key={it.id}
                  onMouseDown={(e) => { if (!isActive) { setActiveId(it.id); } startDrag(e); }}
                  onTouchStart={(e) => { if (!isActive) { setActiveId(it.id); } startDrag(e); }}
                  data-testid={`composition-item-${it.id}`}
                  className={`absolute pointer-events-auto ${interactionMode === "resize" ? "cursor-ns-resize" : "cursor-move"}`}
                  style={{
                    left: `${it.pos.x}%`, top: `${it.pos.y}%`,
                    // Pour les croix point-à-point on n'applique PAS de rotation
                    // au wrapper : Gauche/Droite est géré uniquement par un
                    // miroir scaleX(-1) sur le SVG intérieur (cf. ci-dessous).
                    transform: itDesign.isDotMatrix
                      ? "translate(-50%, -50%)"
                      : `translate(-50%, -50%) rotate(${it.rotation}deg)`,
                    transition: "transform 250ms ease",
                    outline: isActive && items.length > 1 ? "2px dashed rgba(57,255,20,0.6)" : "none",
                    outlineOffset: "8px",
                  }}>
                  {/* Potence (wall-mount bracket) for double-face crosses.
                      Uses 4 dedicated images (one per side); each is placed
                      FLUSH (à ras) against the corresponding edge of the
                      cross. No rotation is applied — each image is already
                      drawn in the correct orientation. */}
                  {it.face === "double" && (() => {
                    const angle = it.potenceRotation || 0;
                    const preset = ({
                      0:   { src: "/crosses-classic/potence-gauche.png", side: "right" },
                      90:  { src: "/crosses-classic/potence-bas.png",    side: "bottom" },
                      180: { src: "/crosses-classic/potence-droite.png", side: "left" },
                      270: { src: "/crosses-classic/potence-haut.png",   side: "top" },
                    })[angle] || { src: "/crosses-classic/potence-gauche.png", side: "right" };
                    // For top/bottom: bracket width matches cross width.
                    // For left/right: bracket height matches cross height.
                    const isVertical = preset.side === "top" || preset.side === "bottom";
                    const longSide = itSize / 2; // half the cross dimension
                    // The image aspect ratio is 119:67 ≈ 1.776 (long side : short side)
                    const shortSide = longSide / (119 / 67);
                    const w = isVertical ? longSide : shortSide;
                    const h = isVertical ? shortSide : longSide;
                    // Position the image so it overlaps the cross by ~50% of
                    // its short side: the bracket "cross plate" tucks behind
                    // the cross while the arm + wall plate stay visible.
                    const overlap = shortSide * 0.5;
                    let tx = 0, ty = 0;
                    if (preset.side === "right")  { tx = itSize / 2 + w / 2 - overlap; ty = 0; }
                    if (preset.side === "left")   { tx = -(itSize / 2 + w / 2 - overlap); ty = 0; }
                    if (preset.side === "top")    { tx = 0; ty = -(itSize / 2 + h / 2 - overlap); }
                    if (preset.side === "bottom") { tx = 0; ty = itSize / 2 + h / 2 - overlap; }
                    return (
                      <img
                        src={preset.src}
                        alt={`Potence ${preset.side}`}
                        draggable={false}
                        data-testid={isActive ? "cross-potence" : undefined}
                        style={{
                          position: "absolute",
                          left: "50%",
                          top: "50%",
                          width: `${w}px`,
                          height: `${h}px`,
                          transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px)`,
                          zIndex: 0,
                          pointerEvents: "none",
                          filter: mode === "day"
                            ? "drop-shadow(0 1px 2px rgba(0,0,0,0.55))"
                            : "drop-shadow(0 1px 3px rgba(0,0,0,0.7))",
                        }}
                      />
                    );
                  })()}
                  {itDesign.customizableSvg ? (
                    <div data-testid={isActive ? "cross-svg" : undefined}
                      style={{
                        width: `${itSize}px`, height: `${itSize}px`,
                        position: "relative",
                        zIndex: 2,
                        transform: itDesign.isDotMatrix
                          ? // Croix point-à-point : pas de rotation, juste
                            // un MIROIR horizontal entre Gauche (scaleX(-1))
                            // et Droite (identité). La grosse patte de fixation
                            // bascule de côté sans pivoter la croix.
                            ((it.rotation || 0) === 270 ? "scaleX(-1)" : "none")
                          : `rotate(${it.rotation || 0}deg)`,
                        transformOrigin: "center center",
                        transition: "transform 0.25s ease-out",
                        // Halo blanc retiré pour les croix point-à-point —
                        // l'utilisateur a demandé un rendu net sans aura
                        // lumineuse parasite autour des LED.
                        filter: itDesign.isDotMatrix
                          ? "none"
                          : mode === "day"
                            ? "drop-shadow(0 0 2px rgba(255,255,255,1)) drop-shadow(0 0 8px rgba(255,255,255,0.85)) drop-shadow(0 0 20px rgba(255,255,255,0.55)) drop-shadow(0 0 40px rgba(255,255,255,0.4))"
                            : "drop-shadow(0 0 3px #fff) drop-shadow(0 0 12px rgba(255,255,255,0.95)) drop-shadow(0 0 28px rgba(255,255,255,0.75)) drop-shadow(0 0 56px rgba(255,255,255,0.55)) drop-shadow(0 0 90px rgba(255,255,255,0.4))",
                      }}>
                      <CustomizableSvg
                        src={itDesign.svgPath}
                        viewBox={itDesign.viewBox}
                        colorOverrides={expandCustomColors(itDesign, it.customColors)}
                        idOverrides={expandIdOverrides(itDesign, it.customColors)}
                        syntheticOverlays={expandSyntheticOverlays(itDesign, it.customColors)}
                        style={{ width: "100%", height: "100%" }}
                      />
                    </div>
                  ) : itIsPreset ? (
                    it.tintColor ? (
                      // Rendu silhouette monochrome (mask-image) — couleur unique au choix
                      <div data-testid={isActive ? "cross-svg" : undefined}
                        style={{
                          width: `${itSize}px`, height: `${itSize}px`,
                          position: "relative",
                          zIndex: 2,
                          backgroundColor: it.tintColor,
                          WebkitMaskImage: `url(${itDesign.imgColor})`,
                          maskImage: `url(${itDesign.imgColor})`,
                          WebkitMaskSize: "contain",
                          maskSize: "contain",
                          WebkitMaskRepeat: "no-repeat",
                          maskRepeat: "no-repeat",
                          WebkitMaskPosition: "center",
                          maskPosition: "center",
                          filter: mode === "day"
                            ? `drop-shadow(0 0 2px ${it.tintColor}) drop-shadow(0 0 8px ${it.tintColor}cc) drop-shadow(0 0 20px ${it.tintColor}aa) drop-shadow(0 0 40px ${it.tintColor}80)`
                            : `drop-shadow(0 0 3px ${it.tintColor}) drop-shadow(0 0 12px ${it.tintColor}f0) drop-shadow(0 0 28px ${it.tintColor}cc) drop-shadow(0 0 56px ${it.tintColor}aa) drop-shadow(0 0 90px ${it.tintColor}80)`,
                        }} />
                    ) : (
                      <img src={itDesign.imgColor} alt={itDesign.label.fr} draggable={false}
                        data-testid={isActive ? "cross-svg" : undefined}
                        style={{
                          width: `${itSize}px`, height: `${itSize}px`, objectFit: "contain",
                          position: "relative",
                          zIndex: 2,
                          filter: mode === "day"
                            ? "drop-shadow(0 0 2px rgba(255,255,255,1)) drop-shadow(0 0 8px rgba(255,255,255,0.85)) drop-shadow(0 0 20px rgba(255,255,255,0.55)) drop-shadow(0 0 40px rgba(255,255,255,0.4))"
                            : "drop-shadow(0 0 3px #fff) drop-shadow(0 0 12px rgba(255,255,255,0.95)) drop-shadow(0 0 28px rgba(255,255,255,0.75)) drop-shadow(0 0 56px rgba(255,255,255,0.55)) drop-shadow(0 0 90px rgba(255,255,255,0.4))",
                        }} />
                    )
                  ) : itDesign.imgBody && itDesign.imgArrow ? (
                    <div data-testid={isActive ? "cross-svg" : undefined} className="relative" style={{ width: `${itSize}px`, height: `${itSize}px`, zIndex: 2 }}>
                      <div style={{
                        position: "absolute", inset: 0, backgroundColor: itColor.hex,
                        WebkitMaskImage: `url(${itDesign.imgBody})`, maskImage: `url(${itDesign.imgBody})`,
                        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                        WebkitMaskSize: "contain", maskSize: "contain",
                        WebkitMaskPosition: "center", maskPosition: "center",
                        filter: itGlow,
                      }} />
                      <div style={{
                        position: "absolute", inset: 0, backgroundColor: itArrowColor.hex,
                        WebkitMaskImage: `url(${itDesign.imgArrow})`, maskImage: `url(${itDesign.imgArrow})`,
                        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                        WebkitMaskSize: "contain", maskSize: "contain",
                        WebkitMaskPosition: "center", maskPosition: "center",
                        filter: itArrowGlow,
                      }} />
                    </div>
                  ) : (
                    <div data-testid={isActive ? "cross-svg" : undefined} style={{
                      width: `${itSize}px`, height: `${itSize}px`,
                      position: "relative",
                      zIndex: 2,
                      backgroundColor: itColor.isMulti ? "#fff" : itColor.hex,
                      WebkitMaskImage: `url(${itDesign.img})`, maskImage: `url(${itDesign.img})`,
                      WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                      WebkitMaskSize: "contain", maskSize: "contain",
                      WebkitMaskPosition: "center", maskPosition: "center",
                      filter: itGlow,
                    }} />
                  )}
                </div>
              );
            })}

            {/* size badge */}
            <div className="absolute bottom-3 left-3 bg-black/65 backdrop-blur border border-white/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white/85">
              {sizeCm} cm{items.length > 1 ? ` · ${items.length} ${lang === "fr" ? "éléments" : "items"}` : ""}
            </div>
          </div>

          {/* Items bar — composition multi-éléments (toujours visible,
              même avec 1 seul élément, pour permettre l'ajout/suppression
              et le suivi global du montage). */}
          {items.length >= 1 && (
            <div data-testid="items-bar" className="mt-3 border border-white/10 bg-[#070707] p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                {lang === "fr" ? `Mes éléments (${items.length}) — cliquer pour modifier` : `My items (${items.length}) — click to edit`}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {items.map((it, idx) => {
                  const isEmpty = !!(it.designId && String(it.designId).startsWith("_empty_"));
                  const itDesign = isEmpty ? null : (ALL_DESIGNS.find((d) => d.id === it.designId) || CROIX_DESIGNS[0]);
                  const isAct = it.id === activeId;
                  return (
                    <div key={it.id} className="relative">
                      <button type="button" onClick={() => setActiveId(it.id)} data-testid={`select-item-${idx}`}
                        className={`flex flex-col items-center gap-1 p-2 border w-[72px] ${isAct ? "border-[#39FF14] bg-[#39FF14]/10" : "border-white/10 hover:border-white/30"}`}>
                        {isEmpty ? (
                          <div className="w-10 h-10 border border-dashed border-white/25 flex items-center justify-center">
                            <X size={16} className="text-zinc-600" />
                          </div>
                        ) : itDesign?.customizableSvg && itDesign?.svgPath ? (
                          <div style={{ width: 40, height: 40, filter: isAct ? "drop-shadow(0 0 4px #39FF14)" : "none" }}>
                            <CustomizableSvg src={itDesign.svgPath} viewBox={itDesign.viewBox} />
                          </div>
                        ) : (
                          <img src={itDesign?.imgColor} alt="" width={40} height={40}
                            style={{ objectFit: "contain", filter: isAct ? "drop-shadow(0 0 4px #39FF14)" : "none" }} />
                        )}
                        <span className={`font-mono text-[9px] uppercase tracking-wider ${isAct ? "text-[#39FF14]" : "text-zinc-400"}`}>{idx + 1}</span>
                      </button>
                      <button type="button" onClick={() => removeItem(it.id)} data-testid={`remove-item-${idx}`}
                        className="absolute -top-1.5 -right-1.5 bg-black border border-red-500/60 text-red-400 hover:bg-red-500/20 w-5 h-5 flex items-center justify-center"
                        title={lang === "fr" ? "Supprimer" : "Remove"}>
                        <Trash2 size={10} />
                      </button>
                    </div>
                  );
                })}
                <button type="button" onClick={addItem} data-testid="add-item-bar"
                  className="flex flex-col items-center justify-center gap-1 p-2 border border-dashed border-[#39FF14]/40 text-[#39FF14] hover:border-[#39FF14] hover:bg-[#39FF14]/5 w-[72px] aspect-square">
                  <Plus size={20} />
                  <span className="font-mono text-[9px] uppercase tracking-wider">{lang === "fr" ? "Ajouter" : "Add"}</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Scene thumbnails groupées en 3 catégories : Façade / Angle / Intérieur.
            Sur mobile (<768px), masqué entièrement — scène forcée à "bardage blanc". */}
        <div className="relative lg:min-h-0 hidden md:block">
          <SceneCategoryPicker
            scenes={SCENES}
            selectedId={sceneId}
            onSelect={(id) => { setSceneId(id); setCustomScene(null); }}
            customScene={customScene}
            onUploadClick={() => sceneFile.current?.click()}
            onClearCustom={() => setCustomScene(null)}
            thumbnailTestIdPrefix="cross-scene"
          />
          <input ref={sceneFile} type="file" accept="image/*" onChange={onUploadScene} className="hidden" />
        </div>
      </div>

      {/* Family tabs */}
      <div className="border border-white/10 bg-[#070707]" data-testid="family-tabs">
        {!isDotMatrixMode && isSimplifiedMode && (
        <div className="flex flex-wrap">
          <button type="button" onClick={() => { setFamily("croix"); setDesignId(CROIX_DESIGNS[0].id); }} data-testid="family-croix"
            className={`flex-1 min-w-[150px] flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest border-r border-b border-white/10 ${family === "croix" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Shapes size={14} />{lang === "fr" ? "Croix" : "Cross"}
          </button>
          <button type="button" onClick={() => { setFamily("ronde"); setDesignId(RONDE_DESIGNS[0].id); }} data-testid="family-ronde"
            className={`flex-1 min-w-[150px] flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest border-b border-white/10 ${family === "ronde" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Circle size={14} />{lang === "fr" ? "Ronde" : "Round"}
          </button>
        </div>
        )}
        {!isDotMatrixMode && !isSimplifiedMode && (
        <div className="flex flex-wrap">
          <button type="button" onClick={() => { setFamily("croix"); setDesignId(LEGACY_CROIX_DESIGNS[0].id); }} data-testid="family-croix"
            className={`flex-1 min-w-[150px] flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest border-r border-b border-white/10 ${family === "croix" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Shapes size={14} />{lang === "fr" ? "Croix" : "Cross"}
          </button>
          <button type="button" onClick={() => { setFamily("croix-fleche"); setDesignId(CROIX_FLECHE_DESIGNS[0].id); }} data-testid="family-croix-fleche"
            className={`flex-1 min-w-[150px] flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest border-r border-b border-white/10 ${family === "croix-fleche" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <ArrowUpRight size={14} />{lang === "fr" ? "Croix Flèche" : "Arrow Cross"}
          </button>
          <button type="button" onClick={() => { setFamily("ronde"); setDesignId(LEGACY_RONDE_DESIGNS[0].id); }} data-testid="family-ronde"
            className={`flex-1 min-w-[150px] flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest border-r border-b border-white/10 ${family === "ronde" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Circle size={14} />{lang === "fr" ? "Ronde / Médaillon" : "Round / Medallion"}
          </button>
          <button type="button" onClick={() => { setFamily("multi"); setDesignId(MULTI_DESIGNS[0].id); }} data-testid="family-multi"
            className={`flex-1 min-w-[150px] flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest border-r border-b border-white/10 ${family === "multi" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Palette size={14} />{lang === "fr" ? "Multi-couleur" : "Multi-color"}
          </button>
          <button type="button" onClick={() => { setFamily("ronde-multi"); setDesignId(RONDE_MULTI_DESIGNS[0].id); }} data-testid="family-ronde-multi"
            className={`flex-1 min-w-[150px] flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest border-r border-b border-white/10 ${family === "ronde-multi" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Circle size={14} />{lang === "fr" ? "Ronde Multi-couleur" : "Round Multi-color"}
          </button>
          <button type="button" onClick={() => { setFamily("angle"); setDesignId(ANGLE_DESIGNS[0].id); }} data-testid="family-angle"
            className={`flex-1 min-w-[150px] flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest border-b border-white/10 ${family === "angle" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Shapes size={14} />{lang === "fr" ? "Enseigne d'angle" : "Corner sign"}
          </button>
        </div>
        )}

        {family === "croix" && isSimplifiedMode && (
          <div className="px-4 pt-3 flex items-center justify-end gap-3 text-[10px] font-mono uppercase tracking-widest" data-testid="croix-legend">
            <span className="flex items-center gap-1.5 text-zinc-400">
              <Palette size={11} className="text-[#39FF14]" />
              {lang === "fr" ? "Personnalisable" : "Customizable"}
            </span>
            <span className="text-zinc-700">|</span>
            <span className="flex items-center gap-1.5 text-zinc-500">
              <Sun size={11} className="text-zinc-500" />
              {lang === "fr" ? "Effet (couleurs imposées)" : "Effect (fixed colors)"}
            </span>
          </div>
        )}

        {/* Barre Simple face / Double face — placée juste sous les onglets
            Croix / Ronde (au-dessus des designs). Demande client : que
            l'utilisateur choisisse d'abord son type de pose avant de
            sélectionner un design. */}
        {initialFamily === "classic" && (family === "croix" || family === "ronde" || family === "croix-fleche") && (
          <div className="px-4 pt-3" data-testid="face-toolbar-top">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="face-simple-top"
                onClick={() => patchActive({ face: "simple" })}
                className={`px-3 py-2.5 border font-mono text-[11px] uppercase tracking-widest transition-all ${
                  (active.face || "simple") === "simple"
                    ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10"
                    : "border-white/10 text-zinc-400 hover:border-white/30"
                }`}>
                <Box size={11} className="inline mr-1 -mt-0.5" />
                {lang === "fr" ? "Simple face" : "Single face"}
              </button>
              <button type="button" data-testid="face-double-top"
                onClick={() => patchActive({ face: "double" })}
                className={`px-3 py-2.5 border font-mono text-[11px] uppercase tracking-widest transition-all ${
                  active.face === "double"
                    ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10"
                    : "border-white/10 text-zinc-400 hover:border-white/30"
                }`}>
                <Box size={11} className="inline mr-1 -mt-0.5" />
                {lang === "fr" ? "Double face" : "Double face"}
              </button>
            </div>
          </div>
        )}

        {/* (Onglets Double/Simple face supprimés — les 2 sections sont
            désormais affichées en permanence dans le grid ci-dessous). */}

        {/* En mode point-à-point : 2 sections empilées verticalement
            (Double face en haut, Simple face en dessous) — affichage
            permanent, sans onglets. */}
        {isDotMatrixMode ? (
          <div className="p-4 space-y-5" data-testid="designs-grid">
            {[
              { key: "double", title: { fr: "DOUBLE FACE", en: "DOUBLE FACE" } },
              { key: "simple", title: { fr: "SIMPLE FACE", en: "SINGLE FACE" } },
              { key: "interieur", title: { fr: "CROIX D'INTÉRIEUR", en: "INDOOR CROSS" } },
            ].map((section) => {
              const items = designsList.filter((d) => !d._empty && d.face === section.key);
              return (
                <div key={section.key} data-testid={`designs-section-${section.key}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14] mb-2 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#39FF14]" />
                    {section.title[lang] || section.title.fr}
                    <span className="text-zinc-500 ml-auto normal-case">{items.length}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {items.map((d) => {
                      const selected = designId === d.id;
                      return (
                        <button key={d.id} type="button" onClick={() => setDesignId(d.id)} data-testid={`design-${d.id}`}
                          className={`relative flex flex-col items-center gap-2 p-3 border ${selected ? "border-[#39FF14] bg-[#39FF14]/5" : "border-white/10 hover:border-white/30"}`}>
                          <div style={{ width: 72, height: 72, filter: selected ? "drop-shadow(0 0 6px #39FF14)" : "none" }}>
                            <CustomizableSvg src={d.svgPath} viewBox={d.viewBox} />
                          </div>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-center leading-tight">
                            {typeof d.label === "object" ? d.label.fr : d.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {/* Tile "AUCUN" en dessous des 2 sections */}
            <div>
              {designsList.filter((d) => d._empty).map((d) => (
                <button key={d.id} type="button" onClick={() => setDesignId(d.id)} data-testid={`design-${d.id}`}
                  className={`relative flex flex-col items-center gap-2 p-3 border w-32 ${designId === d.id ? "border-[#39FF14] bg-[#39FF14]/5" : "border-white/10 hover:border-white/30"}`}>
                  <div className="w-12 h-12 border border-dashed border-white/30 flex items-center justify-center">
                    <X size={20} className="text-zinc-500" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest">AUCUN</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="designs-grid">
          {designsList.map((d) => {
            const selected = designId === d.id;
            const showBadge = family === "croix";
            const isCustomizable = !!(d.customizable || d.customizableSvg);
            return (
              <button key={d.id} type="button" onClick={() => setDesignId(d.id)} data-testid={`design-${d.id}`}
                className={`relative flex flex-col items-center gap-2 p-3 border ${selected ? "border-[#39FF14] bg-[#39FF14]/5" : "border-white/10 hover:border-white/30"}`}>
                {showBadge && !d._empty && (
                  <span
                    title={isCustomizable
                      ? (lang === "fr" ? "Couleur personnalisable" : "Customizable color")
                      : (lang === "fr" ? "Effet (couleurs imposées)" : "Effect (fixed colors)")}
                    className={`absolute top-1.5 right-1.5 inline-flex items-center justify-center w-5 h-5 border ${
                      isCustomizable
                        ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]"
                        : "border-white/15 bg-black/30 text-zinc-500"
                    }`}>
                    {isCustomizable ? <Palette size={10} /> : <Sun size={10} />}
                  </span>
                )}
                {d._empty ? (
                  <div
                    className="flex items-center justify-center"
                    style={{ width: 72, height: 72 }}
                  >
                    <div className="w-12 h-12 border border-dashed border-white/30 flex items-center justify-center">
                      <X size={20} className="text-zinc-500" />
                    </div>
                  </div>
                ) : d.customizableSvg ? (
                  <div style={{ width: 72, height: 72, filter: selected ? "drop-shadow(0 0 6px #39FF14)" : "drop-shadow(0 0 2px rgba(255,255,255,0.2))" }}>
                    <CustomizableSvg src={d.svgPath} viewBox={d.viewBox} />
                  </div>
                ) : (
                  <img src={d.imgColor} alt={d.label.fr}
                    width={72} height={72}
                    style={{ objectFit: "contain", filter: selected ? "drop-shadow(0 0 6px #39FF14)" : "drop-shadow(0 0 2px rgba(255,255,255,0.2))" }} />
                )}
                <span className={`font-mono text-[10px] uppercase tracking-widest text-center leading-tight ${selected ? "text-[#39FF14]" : "text-zinc-400"}`}>{d.label[lang] || d.label.fr}</span>
              </button>
            );
          })}
        </div>
        )}
        {allowTintPicker && (
          <div className="px-4 pb-4 -mt-1 border-t border-white/5 pt-3" data-testid="tint-color-module">
            <div className={labelCls}>
              <Palette size={12} className="text-[#39FF14]" />
              {lang === "fr" ? "Couleur de la croix" : "Cross color"}
              {tintColor && (
                <span className="font-mono text-[10px] text-zinc-500 normal-case">{tintColor}</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button type="button" onClick={() => setTintColor(null)} data-testid="tint-original"
                title={lang === "fr" ? "Couleurs d'origine" : "Original colors"}
                className="px-3 h-7 border font-mono text-[10px] uppercase tracking-widest transition-all flex items-center"
                style={{
                  borderColor: !tintColor ? "#39FF14" : "rgba(255,255,255,0.15)",
                  borderWidth: !tintColor ? 2 : 1,
                  color: !tintColor ? "#39FF14" : "rgb(212 212 216)",
                  background: !tintColor ? "rgba(57,255,20,0.08)" : "transparent",
                }}>
                {lang === "fr" ? "Original" : "Original"}
              </button>
              {activePalette.map((p) => {
                const selected = tintColor && tintColor.toLowerCase() === p.hex.toLowerCase();
                return (
                  <button key={p.id} type="button" onClick={() => setTintColor(p.hex)}
                    data-testid={`tint-${p.id}`}
                    title={p.label}
                    className="w-7 h-7 border transition-all"
                    style={{
                      background: p.hex,
                      borderColor: selected ? "#39FF14" : "rgba(255,255,255,0.15)",
                      borderWidth: selected ? 2 : 1,
                      boxShadow: selected ? `0 0 10px ${p.hex}` : "none",
                      transform: selected ? "scale(1.1)" : "scale(1)",
                    }} />
                );
              })}
            </div>
            <div className="mt-2 font-mono text-[10px] text-zinc-500 leading-relaxed">
              {lang === "fr"
                ? "« Original » conserve les couleurs du design. Cliquez sur une nuance pour le repeindre en une seule couleur."
                : "« Original » keeps the design's colors. Click any shade to repaint in a single color."}
            </div>
          </div>
        )}
      </div>

      {/* Barre de couleur par trait (designs néon trait-par-trait) — même présentation
          pilule que Croix Flèche / Enseigne d'angle, mais une ligne par trait pour
          permettre une couleur différente sur chaque élément du design. */}
      {design?.customizableSvg && design?.colorLayers && isNeonDesign && !design._empty && (
        <div data-testid="neon-color-toolbar" className="border border-[#39FF14]/30 bg-[#39FF14]/[0.03] p-4">
          <div className={labelCls}>
            <Palette size={12} className="text-[#39FF14]" />
            {lang === "fr" ? "Couleur du néon — par trait" : "Neon color — per stroke"}
            <button type="button" onClick={resetCustomColors} data-testid="reset-custom-colors"
              className="ml-auto normal-case text-zinc-500 hover:text-[#39FF14] transition-colors">
              {lang === "fr" ? "Réinitialiser" : "Reset"}
            </button>
          </div>
          <div className="space-y-3">
            {design.colorLayers.map((layer, idx) => {
              const currentHex = (customColors[layer.id] || layer.default || "").toLowerCase();
              return (
                <div key={layer.id} data-testid={`neon-layer-${layer.id}`}>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5">
                    {(layer.label?.[lang] || layer.label?.fr) || `${lang === "fr" ? "Trait" : "Stroke"} ${idx + 1}`}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {COLORS.map((c) => {
                      const selected = currentHex === c.hex.toLowerCase();
                      return (
                        <button key={c.id} type="button" onClick={() => setCustomColor(layer.id, c.hex)}
                          data-testid={`neon-color-${layer.id}-${c.id}`}
                          className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest"
                          style={{
                            borderColor: selected ? "#39FF14" : "rgba(255,255,255,0.12)",
                            background: selected ? "rgba(57,255,20,0.08)" : "transparent",
                            color: selected ? "#fff" : "#9ca3af",
                          }}>
                          <span className="w-5 h-5 rounded-full border border-black/40" style={{
                            background: c.hex,
                            boxShadow: `0 0 12px ${c.hex}`,
                          }} />
                          {c.label.fr}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Module de personnalisation des couleurs (designs SVG multi-couches non-néon) */}
      {design?.customizableSvg && design?.colorLayers && !isNeonDesign && !design._empty && (
        <div className="border border-[#39FF14]/30 bg-[#39FF14]/[0.03] p-4" data-testid="custom-colors-module">
          <div className={labelCls}>
            <Palette size={12} className="text-[#39FF14]" />
            {lang === "fr" ? "Personnaliser les couleurs" : "Customize colors"}
            <button type="button" onClick={resetCustomColors} data-testid="reset-custom-colors"
              className="ml-auto normal-case text-zinc-500 hover:text-[#39FF14] transition-colors">
              {lang === "fr" ? "Réinitialiser" : "Reset"}
            </button>
          </div>
          <div className="space-y-4">
            {design.colorLayers.map((layer) => {
              const rawValue = customColors[layer.id];
              const isTransparent = rawValue === "transparent" || rawValue === "__transparent__";
              const currentHex = rawValue || layer.default;
              // Palette restreinte par slot pour les croix point-à-point :
              // fond et centre utilisent les 4 fonds, LED uses les 3 couleurs LED.
              const layerPalette = isDotMatrix ? dotMatrixPaletteForLayer(layer.id) : activePalette;
              const swatchStyle = isTransparent
                ? { background: "transparent", border: "1px dashed rgba(255,255,255,0.45)", boxShadow: "none" }
                : { background: currentHex, boxShadow: `0 0 8px ${currentHex}` };
              return (
                <div key={layer.id} data-testid={`custom-layer-${layer.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-5 h-5 border border-black/40 rounded-full" style={swatchStyle} />
                    <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-200">
                      {layer.label[lang] || layer.label.fr}
                    </span>
                    <span className="font-mono text-[10px] text-zinc-500 normal-case">
                      {isTransparent ? (lang === "fr" ? "Transparent" : "Transparent") : currentHex}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap pl-7">
                    {layer.allowTransparent && (
                      <button type="button"
                        onClick={() => setCustomColor(layer.id, "transparent")}
                        data-testid={`custom-color-${layer.id}-transparent`}
                        title={lang === "fr" ? "Transparent (creux)" : "Transparent (hollow)"}
                        className="w-7 h-7 border transition-all relative overflow-hidden"
                        style={{
                          background:
                            "repeating-linear-gradient(45deg, rgba(255,255,255,0.10) 0 4px, rgba(255,255,255,0) 4px 8px)",
                          borderColor: isTransparent ? "#39FF14" : "rgba(255,255,255,0.30)",
                          borderWidth: isTransparent ? 2 : 1,
                          borderStyle: "dashed",
                          boxShadow: isTransparent ? "0 0 10px #39FF14" : "none",
                          transform: isTransparent ? "scale(1.1)" : "scale(1)",
                        }}>
                        <X size={12} className="text-zinc-300 absolute inset-0 m-auto" />
                      </button>
                    )}
                    {layerPalette.map((p) => {
                      const selected = !isTransparent && currentHex.toLowerCase() === p.hex.toLowerCase();
                      return (
                        <button key={p.id} type="button" onClick={() => setCustomColor(layer.id, p.hex)}
                          data-testid={`custom-color-${layer.id}-${p.id}`}
                          title={typeof p.label === "object" ? (p.label[lang] || p.label.fr) : p.label}
                          className="w-7 h-7 border transition-all"
                          style={{
                            background: p.hex,
                            borderColor: selected ? "#39FF14" : "rgba(255,255,255,0.15)",
                            borderWidth: selected ? 2 : 1,
                            boxShadow: selected ? `0 0 10px ${p.hex}` : "none",
                            transform: selected ? "scale(1.1)" : "scale(1)",
                          }} />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 font-mono text-[10px] text-zinc-500 leading-relaxed">
            {lang === "fr"
              ? "Cliquez sur une nuance pour modifier l'élément correspondant de la croix. Vos choix sont enregistrés dans le devis."
              : "Click a shade to recolor that element. Your choices are saved with the quote."}
          </div>
        </div>
      )}

      {/* Color pickers — cachés pour les designs preset multi-couleur
          (Multi, Ronde Multi, Flèche Multi 1-6 qui ont `preset: true`).
          Affichés pour les designs personnalisables Croix Flèche 1 & 2 avec
          un label clair "Trait corps de croix" pour les distinguer du picker
          flèche en-dessous. */}
      {!isPresetDesign && !_designPick._empty && (<>
      <div data-testid="color-toolbar">
        <div className={labelCls}>
          <Palette size={12} className="text-[#39FF14]" />
          {family === "angle"
            ? (lang === "fr" ? "Couleur intérieure" : "Inner color")
            : family === "croix-fleche"
              ? (lang === "fr" ? "Trait corps de croix" : "Cross body stroke")
              : (lang === "fr" ? "Couleur du néon" : "Neon color")}
          {design?.hasArrow && family !== "croix-fleche" ? (
            <span className="text-zinc-500 normal-case">— {family === "angle" ? (lang === "fr" ? "croix" : "cross") : "corps"}</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {COLORS.map((c) => {
            const selected = colorId === c.id;
            const bg = c.isMulti
              ? "conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)"
              : c.hex;
            return (
              <button key={c.id} type="button" onClick={() => setColorId(c.id)} data-testid={`color-${c.id}`}
                className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest"
                style={{
                  borderColor: selected ? "#39FF14" : "rgba(255,255,255,0.12)",
                  background: selected ? "rgba(57,255,20,0.08)" : "transparent",
                  color: selected ? "#fff" : "#9ca3af",
                }}>
                <span className="w-5 h-5 rounded-full border border-black/40" style={{
                  background: bg,
                  boxShadow: c.isMulti ? "0 0 12px rgba(255,0,255,0.6)" : `0 0 12px ${c.hex}`,
                }} />
                {c.label.fr}
              </button>
            );
          })}
        </div>
      </div>
      </>)}

      {/* Color picker — 2ème couche : flèche (croix-fleche 1/2) ou contour (angle).
          Caché sur les designs preset (`isPresetDesign` couvre déjà Flèche Multi 1-6). */}
      {design?.hasArrow && !isPresetDesign && !_designPick._empty && (
        <div data-testid="arrow-color-toolbar" className="border border-[#39FF14]/30 bg-[#39FF14]/[0.04] p-4">
          <div className={labelCls}>
            <Palette size={12} className="text-[#39FF14]" />
            {family === "angle"
              ? (lang === "fr" ? "Couleur du contour" : "Outline color")
              : (lang === "fr" ? "Trait flèche" : "Arrow stroke")}
            <span className="text-zinc-500 normal-case ml-1">— {lang === "fr" ? "distincte du corps" : "distinct from body"}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {COLORS.map((c) => {
              const selected = arrowColorId === c.id;
              return (
                <button key={c.id} type="button" onClick={() => setArrowColorId(c.id)} data-testid={`arrow-color-${c.id}`}
                  className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest"
                  style={{
                    borderColor: selected ? "#39FF14" : "rgba(255,255,255,0.12)",
                    background: selected ? "rgba(57,255,20,0.08)" : "transparent",
                    color: selected ? "#fff" : "#9ca3af",
                  }}>
                  <span className="w-5 h-5 rounded-full border border-black/40" style={{
                    background: c.hex,
                    boxShadow: `0 0 12px ${c.hex}`,
                  }} />
                  {c.label.fr}
                </button>
              );
            })}
          </div>
          <div className="mt-2 font-mono text-[10px] text-zinc-500 leading-relaxed">
            {lang === "fr"
              ? "La flèche sera réalisée dans cette teinte distincte du corps. L'information est transmise au devis."
              : "The arrow will use this distinct neon color from the body. Captured in the quote."}
          </div>
        </div>
      )}

      {/* Rotation toolbar — pour Croix Flèche (4 directions) OU Croix
          point-à-point EXTÉRIEURE (Gauche/Droite uniquement, le support
          mural change de côté). Masqué pour les croix d'intérieur :
          pas de potence, donc pas d'orientation. */}
      {(family === "croix-fleche" || (isDotMatrixMode && _designPick?.face && _designPick.face !== "interieur")) && (
        <div data-testid="rotation-toolbar">
          <div className={labelCls}>
            <RotateCw size={12} className="text-[#39FF14]" />
            {lang === "fr" ? "Orientation de la croix" : "Cross orientation"} — <span className="text-[#39FF14]">{rotation}°</span>
          </div>
          <div className={`grid gap-2 ${isDotMatrixMode ? "grid-cols-2" : "grid-cols-4"}`}>
            {(isDotMatrixMode
              ? [
                  { v: 270, fr: "← Gauche", en: "← Left" },
                  { v: 90,  fr: "→ Droite", en: "→ Right" },
                ]
              : [
                  { v: 0,   fr: "↑ Haut",   en: "↑ Up" },
                  { v: 180, fr: "↓ Bas",    en: "↓ Down" },
                  { v: 270, fr: "← Gauche", en: "← Left" },
                  { v: 90,  fr: "→ Droite", en: "→ Right" },
                ]
            ).map((opt) => (
              <button key={opt.v} type="button" onClick={() => setRotation(opt.v)} data-testid={`rotation-${opt.v}`}
                className={`px-2 py-3 border font-mono text-[10px] uppercase tracking-widest ${rotation === opt.v ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                {lang === "fr" ? opt.fr : opt.en}
              </button>
            ))}
          </div>
          <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
            {isDotMatrixMode
              ? (lang === "fr"
                  ? "↳ Le support de fixation bascule de côté"
                  : "↳ Mounting bracket switches side")
              : (lang === "fr"
                  ? "↳ Haut ↔ Bas (180°) · Gauche ↔ Droite (180°)"
                  : "↳ Up ↔ Down (180°) · Left ↔ Right (180°)")}
          </div>
        </div>
      )}

      {/* Size — presets selon le type de croix point-à-point :
          • Extérieur (DF/SF avec potence) : 60 / 80 cm
          • Intérieur (sans potence)        : 30 / 50 / 60 cm
          Sinon (autres familles) : slider + saisie libre + presets. */}
      {!_designPick._empty && isDotMatrixMode && (() => {
        const isInterior = _designPick?.face === "interieur";
        const sizes = isInterior ? DOTMATRIX_SIZES_INT : DOTMATRIX_SIZES_EXT;
        return (
        <div data-testid="size-toolbar-dotmatrix">
          <div className={labelCls}>
            <Ruler size={12} className="text-[#39FF14]" />
            {lang === "fr" ? "Dimensions de la croix" : "Cross dimensions"} —{" "}
            <span className="text-[#39FF14] font-mono" data-testid="cross-dimensions">
              {sizeCm} × {sizeCm} cm
            </span>
          </div>
          <div className={`grid gap-2 ${sizes.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
            {sizes.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setSizeCm(v)}
                data-testid={`size-${v}`}
                className={`py-4 border font-mono text-sm uppercase tracking-widest transition-all ${
                  sizeCm === v
                    ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]"
                    : "border-white/15 text-zinc-400 hover:border-[#39FF14]/60 hover:text-[#39FF14]"
                }`}
              >
                {v} × {v} cm
              </button>
            ))}
          </div>
        </div>
        );
      })()}
      {!_designPick._empty && !isDotMatrixMode && (
      <div data-testid="size-toolbar">
        <div className={labelCls}>
          <Ruler size={12} className="text-[#39FF14]" />
          {lang === "fr" ? "Dimensions de la croix" : "Cross dimensions"} —{" "}
          <span className="text-[#39FF14] font-mono" data-testid="cross-dimensions">
            {sizeCm} × {sizeCm} cm
          </span>
        </div>
        {(() => {
          // Sur la page classique, la dimension est plafonnée à 80 cm (spec
          // client : pas de 100 cm). Sur les autres familles, la barre va
          // jusqu'à 150 cm.
          const maxCm = initialFamily === "classic" ? 80 : 150;
          const inputMax = initialFamily === "classic" ? 80 : 200;
          const presets = initialFamily === "classic"
            ? [30, 40, 50, 60, 70, 80]
            : [30, 40, 50, 60, 70, 80, 100];
          return (
            <>
              <div className="flex items-center gap-3 border border-white/10 bg-[#0a0a0a] px-4 py-3">
                <span className="font-mono text-[10px] text-zinc-500">20</span>
                <input type="range" min={20} max={maxCm} step={1} value={Math.min(maxCm, Math.max(20, sizeCm))}
                  onChange={(e) => setSizeCm(parseInt(e.target.value, 10))}
                  data-testid="size-slider"
                  className="flex-1 accent-[#39FF14]" />
                <span className="font-mono text-[10px] text-zinc-500">{maxCm}</span>
                <input
                  type="number"
                  min={20}
                  max={inputMax}
                  step={1}
                  value={sizeCm}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setSizeCm(Math.min(inputMax, Math.max(20, v)));
                  }}
                  data-testid="size-custom-input"
                  className="w-20 bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-sm text-[#39FF14] text-right focus:outline-none focus:border-[#39FF14]"
                />
                <span className="font-mono text-xs text-zinc-400">cm</span>
              </div>
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {presets.map((v) => (
                  <button key={v} type="button" onClick={() => setSizeCm(v)} data-testid={`size-${v}`}
                    className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${sizeCm === v ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                    {v} cm
                  </button>
                ))}
              </div>
            </>
          );
        })()}
        <div className="mt-1.5 font-mono text-[10px] text-zinc-500 leading-relaxed">
          {lang === "fr"
            ? "Saisissez la dimension exacte (cm). La croix étant carrée, hauteur = largeur."
            : "Enter the exact dimension (cm). Cross is square, so height = width."}
        </div>
      </div>
      )}

      {/* Position de la potence — Affiché UNIQUEMENT quand le type de pose
          actif est "double face" (la barre Simple/Double face a été
          remontée en haut, juste sous les onglets Croix / Ronde). */}
      {initialFamily === "classic" && !_designPick._empty && (family === "croix" || family === "ronde" || family === "croix-fleche") && active.face === "double" && (
        <div data-testid="face-toolbar">
          <div className="border border-white/10 bg-[#0a0a0a] p-3" data-testid="potence-controls">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
              {lang === "fr" ? "Côté de la potence" : "Bracket side"}
            </div>
            {/* 4 sides: Top / Right / Bottom / Left */}
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { angle: 270, label: { fr: "Haut", en: "Top" }, icon: ChevronUp },
                { angle: 0,   label: { fr: "Droite", en: "Right" }, icon: ChevronRight },
                { angle: 90,  label: { fr: "Bas", en: "Bottom" }, icon: ChevronDown },
                { angle: 180, label: { fr: "Gauche", en: "Left" }, icon: ChevronLeft },
              ].map(({ angle, label, icon: Icon }) => {
                const selected = (active.potenceRotation || 0) === angle;
                return (
                  <button key={angle} type="button"
                    onClick={() => patchActive({ potenceRotation: angle })}
                    data-testid={`potence-side-${angle}`}
                    className={`flex flex-col items-center gap-1 px-2 py-2 border font-mono text-[9px] uppercase tracking-widest transition-all ${
                      selected
                        ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10"
                        : "border-white/10 text-zinc-400 hover:border-white/30"
                    }`}>
                    <Icon size={14} />
                    {label[lang] || label.fr}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
